import React, { FC, useEffect, useRef, useState } from "react";
import {
  Box,
  Button,
  Table,
  TableBody,
  TableCell,
  TableRow,
  TextField,
  Typography,
  Chip,
  AccordionDetails,
  MenuItem,
  Checkbox,
  Grid,
  CircularProgress,
  FormControl,
  ListItemText,
  Select,
  OutlinedInput,
  DialogActions,
  Dialog,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Autocomplete,
  InputLabel,
  FormHelperText,
} from "@mui/material";
import { useParams } from "react-router-dom";
import PageContainer from "../../../components/pageContainer";
import { PictureAsPdf, TableView, ExpandMore, Add, Delete, FileUpload } from "@mui/icons-material";
import useReport from "../../../hooks/useReport";
import { AdminUpdateReportAdminReportReportIdPutData, IndoorUnitPhotoSetResponse, OutdoorUnitPhotoSetResponse, PhotoSetResponse, ReportResponse } from "../../../client";
import { downloadPdf, downloadExcel, downloadDefaultPdf } from "../../../utils/downloadUtils";
import ImageSelector from "../../../components/imageSelector";
import FixedAspectImage from "../../../components/fixedAspectImage";
import { compressImage } from "../../../utils/imageCompression";
import FloorPlanEditorWrapper from "../../../components/floorPlanEditorWrapper";
import { KEPT_FLOOR_PLAN_MESSAGE, keepFloorPlansWithBackgroundLoadError } from "../../../utils/floorPlanSave";
import { MenuProps, REFRIGERANT_OPTIONS, TYPE_OPTIONS, WORK_CHECK_OPTIONS, WORK_LOCATION_CHECK_OPTIONS } from "../../../constants";
import { StyledAccordion, StyledAccordionSummary, StyledTableCell } from "../../../styles/commonStyles";
import { useManufacturerOptions, useWorkTypeOptions, useSalesPersonOptions, useManufacturerOptionsWithId, useWorkTypeOptionsWithId, useSalesPersonOptionsWithId } from "../../../hooks/useMaster";
import { ImageChange, PhotoSetChangesWithMiddle, PhotoUpdate } from "../../../types/photo";
import { isValidFieldValue } from "../../../utils/typeGuards";
import { useFormComponents } from "../../../hooks/useFormComponents";
import { buildMonthUnknown, isMonthUnknown, looksLikeUnknownMonthTypo, validateMonthUnknownInput, yearPartOf } from "../../../utils/manufacturingDate";
import { imageSrcForLoad } from "../../../utils/imageUrl";


const WorkCheckButton: FC<{
  value: string;
  isActive: boolean;
  onClick: () => void;
  disabled?: boolean;
}> = ({ value, isActive, onClick, disabled }) => (
  <Button
    onClick={onClick}
    disabled={disabled}
    sx={(theme) => ({
      minWidth: 'auto',
      padding: '4px 8px',
      color: isActive ? "#1976d2" : theme.palette.text.secondary,
      fontWeight: isActive ? 900 : 400,
      fontSize: '24px', // サイズをより大きく
      fontFamily: value === 'ー' ? 'inherit' : '"Segoe UI Symbol", "Noto Sans", sans-serif', // ○と△用のフォントを指定
      lineHeight: 1,
      '&:hover': {
        backgroundColor: 'transparent',
      }
    })}
  >
    {value}
  </Button>
);



const ReportAirconDetail: FC = () => {
  const [isEditing, setIsEditing] = useState(false);
  const [data, setData] = useState<ReportResponse | null>(null);
  const { id } = useParams();
  const { getReportById, updateReport, getImageUploadUrl, deleteFile, updateReportStatus, updateReportSalesStatus } = useReport();
  const { options: manufacturerOptions, loading: manufacturerLoading } = useManufacturerOptions();
  const { options: workTypeOptions, loading: workTypeLoading } = useWorkTypeOptions();
  const { options: salesPersonOptions, loading: salesPersonLoading } = useSalesPersonOptions();
  const { optionsWithId: manufacturerOptionsWithId } = useManufacturerOptionsWithId();
  const { optionsWithId: workTypeOptionsWithId } = useWorkTypeOptionsWithId();
  const { optionsWithId: salesPersonOptionsWithId } = useSalesPersonOptionsWithId();
  const idNum = Number(id);
  const [originalData, setOriginalData] = useState<ReportResponse | null>(null);
  const [photoChanges, setPhotoChanges] = useState<Map<number, PhotoSetChangesWithMiddle>>(new Map());
  const [isLoading, setIsLoading] = useState(false);
  const [expandedUnits, setExpandedUnits] = useState<Record<string, boolean>>({});
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<{
    type: 'worker' | 'indoor' | 'outdoor' | 'other' | 'photo' | 'floor_plan' | 'indoor_photo_set' | 'outdoor_photo_set' | 'other_photo_set';
    index: number;
    parentIndex?: number; // 内機・外機・その他作業の写真セットの場合、親のユニットのインデックス
  } | null>(null);
  const floorPlanEditorRefs = useRef<{ [key: number]: any }>({});
  const { renderEditableDate } = useFormComponents(isEditing);

  // 削除確認ダイアログを開く関数
  const handleDeleteClick = (
    type: 'worker' | 'indoor' | 'outdoor' | 'other' | 'photo' | 'floor_plan' | 'indoor_photo_set' | 'outdoor_photo_set' | 'other_photo_set',
    index: number,
    parentIndex?: number
  ) => {
    setDeleteTarget({ type, index, parentIndex });
    setDeleteDialogOpen(true);
  };


  // 削除を実行する関数
  const handleDelete = () => {
    if (!deleteTarget || !data) return;

    switch (deleteTarget.type) {
      case 'worker':
        if (data.workers) {
          const newWorkers = [...data.workers];
          newWorkers.splice(deleteTarget.index, 1);
          setData({ ...data, workers: newWorkers });
        }
        break;
      case 'indoor':
        if (data.indoor_units_list) {
          const newUnits = [...data.indoor_units_list];
          newUnits.splice(deleteTarget.index, 1);
          setData({ ...data, indoor_units_list: newUnits });
        }
        break;
      case 'outdoor':
        if (data.outdoor_units_list) {
          const newUnits = [...data.outdoor_units_list];
          newUnits.splice(deleteTarget.index, 1);
          setData({ ...data, outdoor_units_list: newUnits });
        }
        break;
      case 'other':
        if (data.other_works) {
          const newWorks = [...data.other_works];
          newWorks.splice(deleteTarget.index, 1);
          setData({ ...data, other_works: newWorks });
        }
        break;
      case 'photo':
        if (data.photo_sets) {
          const newPhotos = [...data.photo_sets];
          newPhotos.splice(deleteTarget.index, 1);
          setData({ ...data, photo_sets: newPhotos });
        }
        break;
      case 'floor_plan':
        if (data.floor_plans) {
          const newFloorPlans = [...data.floor_plans];
          newFloorPlans.splice(deleteTarget.index, 1);
          setData({ ...data, floor_plans: newFloorPlans });
        }
        break;
      case 'indoor_photo_set':
        // 内機ユニット内の個別写真セットを削除
        if (data.indoor_units_list && deleteTarget.parentIndex !== undefined) {
          const newUnits = [...data.indoor_units_list];
          const unit = newUnits[deleteTarget.parentIndex];
          if (unit.photo_sets) {
            const newPhotoSets = [...unit.photo_sets];
            newPhotoSets.splice(deleteTarget.index, 1);
            newUnits[deleteTarget.parentIndex] = { ...unit, photo_sets: newPhotoSets };
            setData({ ...data, indoor_units_list: newUnits });
          }
        }
        break;
      case 'outdoor_photo_set':
        // 外機ユニット内の個別写真セットを削除
        if (data.outdoor_units_list && deleteTarget.parentIndex !== undefined) {
          const newUnits = [...data.outdoor_units_list];
          const unit = newUnits[deleteTarget.parentIndex];
          if (unit.photo_sets) {
            const newPhotoSets = [...unit.photo_sets];
            newPhotoSets.splice(deleteTarget.index, 1);
            newUnits[deleteTarget.parentIndex] = { ...unit, photo_sets: newPhotoSets };
            setData({ ...data, outdoor_units_list: newUnits });
          }
        }
        break;
      case 'other_photo_set':
        // その他作業内の個別写真セットを削除
        if (data.other_works && deleteTarget.parentIndex !== undefined) {
          const newWorks = [...data.other_works];
          const work = newWorks[deleteTarget.parentIndex];
          if (work.photo_sets) {
            const newPhotoSets = [...work.photo_sets];
            newPhotoSets.splice(deleteTarget.index, 1);
            newWorks[deleteTarget.parentIndex] = { ...work, photo_sets: newPhotoSets };
            setData({ ...data, other_works: newWorks });
          }
        }
        break;
    }
    setDeleteDialogOpen(false);
    setDeleteTarget(null);
  };


  // 削除ダイアログのコンポーネント
  const DeleteConfirmDialog = () => (
    <Dialog
      open={deleteDialogOpen}
      onClose={() => setDeleteDialogOpen(false)}
    >
      <DialogTitle>削除の確認</DialogTitle>
      <DialogContent>
        <DialogContentText>
          {deleteTarget && `この${deleteTarget.type === 'worker' ? '作業者' :
            deleteTarget.type === 'indoor' ? '内機' :
              deleteTarget.type === 'outdoor' ? '外機' :
                deleteTarget.type === 'indoor_photo_set' ? '内機写真セット' :
                  deleteTarget.type === 'outdoor_photo_set' ? '外機写真セット' :
                    deleteTarget.type === 'other_photo_set' ? 'その他作業写真セット' :
                      deleteTarget.type === 'other' ? 'その他作業' :
                        deleteTarget.type === 'floor_plan' ? '見取り図' :
                          '写真'
            }を削除してもよろしいですか？`}
        </DialogContentText>
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setDeleteDialogOpen(false)}>キャンセル</Button>
        <Button onClick={handleDelete} color="error">削除</Button>
      </DialogActions>
    </Dialog>
  );

  const SectionHeader = ({ index, type, managementNo }: {
    index: number,
    type: 'worker' | 'indoor' | 'outdoor' | 'other' | 'photo' | 'floor_plan',
    managementNo?: string
  }) => (
    <Box
      display="flex"
      justifyContent="space-between"
      alignItems="center"
      width="100%"
    >
      <Typography>
        {type === 'worker' ? `作業者 ${index + 1}` :
          type === 'indoor' ? `内機 ${index + 1}${managementNo ? ` - ${managementNo}` : ''}` :
            type === 'outdoor' ? `外機 ${index + 1}${managementNo ? ` - ${managementNo}` : ''}` :
              type === 'other' ? `作業 ${index + 1}` :
                type === 'floor_plan' ? `見取り図 ${index + 1}` :
                  `写真セット ${index + 1}`}
      </Typography>
      {isEditing && (
        <Button
          startIcon={<Delete />}
          color="error"
          size="small"
          onClick={(e) => {
            e.stopPropagation();
            handleDeleteClick(type, index);
          }}
          sx={{ ml: 2 }}
        >
          削除
        </Button>
      )}
    </Box>
  );

  // 展開状態を切り替える関数を修正 (indoor/outdoor を区別するために prefix を追加)
  const handleUnitAccordionChange = (prefix: string, unitId: number) =>
    (event: React.SyntheticEvent, isExpanded: boolean) => {
      setExpandedUnits(prev => ({ ...prev, [`${prefix}-${unitId}`]: isExpanded }));
    };



  useEffect(() => {
    if (idNum) {
      getData();
    }
  }, [idNum]);

  const getData = async () => {
    if (!idNum) return;
    try {
      const res = await getReportById({ reportId: idNum });
      if (res) {
        setData(res);
        setOriginalData(res); // 元データも保存
      }
    } catch (error) {
      console.error("Failed to get report detail", error);
    }
  };

  // キャンセル処理を追加
  const handleCancel = () => {
    if (originalData) {
      setData(JSON.parse(JSON.stringify(originalData))); // ディープコピーを作成
    }
    // 編集中に選択した未保存の写真変更(photoChanges)も破棄する。
    // クリアしないと、キャンセル後に再編集して保存した際に、
    // 前回キャンセルしたはずの画像がアップロードされてしまう。
    setPhotoChanges(new Map());
    setIsEditing(false);
  };

  // 編集モード開始時の処理を追加
  const handleStartEditing = () => {
    setOriginalData(JSON.parse(JSON.stringify(data))); // 現在のデータのディープコピーを保存
    setIsEditing(true);
  };

  // PDFダウンロードの処理
  const handleDownloadPdf = async () => {
    if (!data) return;

    await downloadPdf(
      {
        id: idNum,
        destinationName: data.delivery_destination,
        workDate: data.start_date,
        setLoading: setIsLoading
      },
      'report'
    );
  };

  const handleDownloadDefaultPdf = async () => {
    if (!data) return;

    await downloadDefaultPdf(
      {
        id: idNum,
        destinationName: data.delivery_destination,
        workDate: data.start_date,
        setLoading: setIsLoading
      },
      'report'
    );
  };


  const handleDownloadExcel = async () => {
    if (!data) return;

    await downloadExcel(
      {
        id: idNum,
        destinationName: data.delivery_destination,
        workDate: data.start_date,
        setLoading: setIsLoading
      },
      'report'
    );
  };


  const handlePhotoChange = async (
    photoSet: PhotoSetResponse,
    type: 'before' | 'after' | 'middle',
    file: File | null,
    onChange: (updatedPhotoSet: PhotoSetResponse) => void  // onChange パラメータを追加
  ) => {
    const changes = photoChanges.get(photoSet.id) || {} as PhotoSetChangesWithMiddle;

    // S3のURLのみを保持し、Blob URLは保持しない
    const getValidS3Url = (url: string | null | undefined): string | null => {
      if (!url) return null;
      return url.startsWith('https://') ? url : null;
    };

    if (type === 'before') {
      changes.before = {
        oldUrl: getValidS3Url(photoSet.before_image_path),
        newFile: file,
        newUrl: null
      };
    } else if (type === 'after') {
      changes.after = {
        oldUrl: getValidS3Url(photoSet.after_image_path),
        newFile: file,
        newUrl: null
      };
    } else {
      changes.middle = {
        oldUrl: getValidS3Url(photoSet.middle_image_path),
        newFile: file,
        newUrl: null
      };
    }

    setPhotoChanges(new Map(photoChanges.set(photoSet.id, changes)));

    // UIの更新用に一時的なBlob URLを生成
    if (file) {
      const blobUrl = URL.createObjectURL(file);
      if (type === 'before') {
        onChange({
          ...photoSet,
          before_image_path: blobUrl
        });
      } else {
        onChange({
          ...photoSet,
          after_image_path: blobUrl
        });
      }
    }
  };

  // Blob URLのクリーンアップ用関数
  const cleanupBlobUrl = (url: string | null | undefined) => {
    if (url?.startsWith('blob:')) {
      URL.revokeObjectURL(url);
    }
  };

  // 画像削除時に、その写真セットの保留中の変更(未アップロードの新規画像)を破棄する。
  // これを呼ばずに写真だけnullにすると、新規選択→削除の順で操作した際にphotoChangesに
  // 残った新規ファイルがsubmit時にアップロードされ、削除したはずの画像が復活してしまう。
  const clearPhotoChange = (photoSetId: number, type: 'before' | 'middle' | 'after') => {
    setPhotoChanges(prev => {
      const changes = prev.get(photoSetId);
      if (!changes || !changes[type]) return prev;
      const next = new Map(prev);
      const updated = { ...changes };
      delete updated[type];
      next.set(photoSetId, updated);
      return next;
    });
  };

  // 常に最新のdataを参照するためのref（クリーンアップをマウント時の1回だけ登録するため）
  const dataRef = useRef(data);
  useEffect(() => {
    dataRef.current = data;
  }, [data]);

  const revokeAllPhotoBlobs = (target: ReportResponse | null | undefined) => {
    target?.indoor_units_list?.forEach(unit => {
      unit.photo_sets?.forEach(photo => {
        cleanupBlobUrl(photo.before_image_path);
        cleanupBlobUrl(photo.after_image_path);
      });
    });
    target?.outdoor_units_list?.forEach(unit => {
      unit.photo_sets?.forEach(photo => {
        cleanupBlobUrl(photo.before_image_path);
        cleanupBlobUrl(photo.after_image_path);
      });
    });
    target?.photo_sets?.forEach(photo => {
      cleanupBlobUrl(photo.before_image_path);
      cleanupBlobUrl(photo.after_image_path);
    });
  };

  // コンポーネントのアンマウント時にBlob URLを解放。
  // 依存配列を[data]にすると編集ごとに前回分のcleanupが走り、表示中の
  // 同一blob URLを誤って解放してしまう（プレビューが壊れる）ため、
  // マウント時の1回だけ登録し、アンマウント時にrefから最新のdataを読む。
  useEffect(() => {
    return () => {
      revokeAllPhotoBlobs(dataRef.current);
    };
  }, []);

  // 同一ルート内でidNum（表示中のreport）が切り替わった場合にも、
  // 切り替え前のreportのBlob URLを解放する。このページはreport一覧を経由せず
  // idだけ変わる遷移をした場合コンポーネントが再マウントされないため、
  // アンマウント時のcleanupだけでは切り替え前のblobが解放されずに残ってしまう。
  useEffect(() => {
    return () => {
      revokeAllPhotoBlobs(dataRef.current);
    };
  }, [idNum]);




  const uploadPhoto = async (change: ImageChange): Promise<string | null> => {
    if (!change.newFile) return null;

    try {
      // 画像を圧縮（パラメータは関数内で固定）
      const compressResult = await compressImage(change.newFile);

      // 圧縮された画像ファイルを使用
      const compressedFile = compressResult.file;

      // すでに存在する useReport フックから取得した関数を使用
      const uploadUrlResponse = await getImageUploadUrl({
        filename: change.newFile.name
      });

      if (!uploadUrlResponse?.upload_url) {
        console.error('No upload URL received');
        return null;
      }

      const { url, fields } = uploadUrlResponse.upload_url;
      // FormDataの作成
      const formData = new FormData();
      // 認証フィールドを追加 - 型エラーを修正
      Object.entries(fields).forEach(([key, value]) => {
        // value が string型であることを確認
        formData.append(key, value as string);
      });
      // 圧縮されたファイルを追加
      formData.append('file', compressedFile);

      // S3へのアップロード
      const response = await fetch(url, {
        method: 'POST',
        body: formData
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('Upload error response:', errorText);
        throw new Error(`Upload failed: ${response.status} ${response.statusText}`);
      }

      // アップロード成功後のURLを構築
      const finalUrl = `${url}${fields.key}`;
      return finalUrl;

    } catch (error) {
      console.error('Error uploading image:', error);
      throw error;
    }
  };

  // 写真セット情報の更新処理関数は同じ
  const getUpdatedPhotoSets = (photoSets: PhotoSetResponse[] | null | undefined): PhotoUpdate[] => {
    if (!photoSets) return [];


    return photoSets.map(photo => {
      const changes = photoChanges.get(photo.id);

      // before_image_path の処理
      let beforeImagePath: string | null = null;
      if (changes?.before?.newUrl && typeof changes.before.newUrl === 'string') {
        beforeImagePath = changes.before.newUrl;
      } else if (typeof photo.before_image_path === 'string') {
        if (photo.before_image_path.startsWith('https://')) {
          beforeImagePath = photo.before_image_path;
        }
      }

      // after_image_path の処理
      let afterImagePath: string | null = null;
      if (changes?.after?.newUrl && typeof changes.after.newUrl === 'string') {
        afterImagePath = changes.after.newUrl;
      } else if (typeof photo.after_image_path === 'string') {
        if (photo.after_image_path.startsWith('https://')) {
          afterImagePath = photo.after_image_path;
        }
      }

      // middle_image_path の処理
      let middleImagePath: string | null = null;
      if (changes?.middle?.newUrl && typeof changes.middle.newUrl === 'string') {
        middleImagePath = changes.middle.newUrl;
      } else if (typeof photo.middle_image_path === 'string') {
        if (photo.middle_image_path.startsWith('https://')) {
          middleImagePath = photo.middle_image_path;
        } else {
          console.log('Invalid middle URL format:', photo.middle_image_path);
        }
      }

      const result = {
        title: photo.title || '',
        before_image_path: beforeImagePath,
        before_comment: photo.before_comment || '',
        middle_image_path: middleImagePath,
        middle_comment: photo.middle_comment || '',
        after_image_path: afterImagePath,
        after_comment: photo.after_comment || ''
      };

      return result;
    });
  };

  // ReportAirconDetail.tsx内に追加する関数
  // フォールバックとして見取り図のキャプチャを作成する関数
  const createFallbackCapture = async (plan: any) => {
    // 新しいキャンバスを作成
    const canvas = document.createElement('canvas');
    canvas.width = 600;
    canvas.height = 250;
    const ctx = canvas.getContext('2d');

    if (ctx) {
      // 背景色を白に設定
      ctx.fillStyle = '#FFFFFF';
      ctx.fillRect(0, 0, canvas.width, canvas.height);

      // グリッドを描画（マス目の背景）
      ctx.strokeStyle = 'rgba(0, 0, 0, 0.2)';
      ctx.lineWidth = 1;

      const gridSize = 25;
      for (let i = 0; i <= canvas.width; i += gridSize) {
        ctx.beginPath();
        ctx.moveTo(i, 0);
        ctx.lineTo(i, canvas.height);
        ctx.stroke();
      }

      for (let i = 0; i <= canvas.height; i += gridSize) {
        ctx.beginPath();
        ctx.moveTo(0, i);
        ctx.lineTo(canvas.width, i);
        ctx.stroke();
      }

      // 背景画像があれば描画
      if (plan.background_image_url) {
        // 背景画像をロードして描画
        try {
          const img = new Image();
          // エディタと同じく CORS 付きで読む。付けないと canvas が汚れて撮影（toBlob）が失敗するうえ、
          // CORS なしの応答（S3 は Vary を付けない）がキャッシュに入り、同じ署名付き URL をエディタが
          // CORS 付きで読み直したときに失敗して背景が消える
          img.crossOrigin = "anonymous";

          await new Promise<void>((resolve, reject) => {
            img.onload = () => {
              ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
              resolve();
            };
            img.onerror = (e) => {
              console.error('背景画像のロードに失敗:', e);
              resolve(); // エラーでも続行
            };
            // 署名の無い URL にだけキャッシュ回避のタイムスタンプを足す（署名付き URL は足すと 403）
            img.src = imageSrcForLoad(plan.background_image_url);

            // 3秒後にタイムアウト
            setTimeout(() => {
              if (!img.complete) {
                console.warn('画像のロードがタイムアウトしました');
                resolve();
              }
            }, 3000);
          });
        } catch (err) {
          console.error('背景画像の描画でエラー:', err);
        }
      }
      // image_dataがある場合はそれを描画（background_image_urlの代わり）
      else if (plan.image_data) {
        try {
          const img = new Image();

          await new Promise<void>((resolve) => {
            img.onload = () => {
              ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
              resolve();
            };
            img.onerror = () => {
              console.error('image_dataからの画像ロードに失敗');
              resolve(); // エラーでも続行
            };
            img.src = `data:image/jpeg;base64,${plan.image_data}`;

            // 3秒後にタイムアウト
            setTimeout(() => {
              if (!img.complete) {
                console.warn('image_dataからの画像ロードがタイムアウトしました');
                resolve();
              }
            }, 3000);
          });
        } catch (err) {
          console.error('image_dataからの画像描画でエラー:', err);
        }
      }

      // キャンバスをBlobに変換してアップロード
      return new Promise<void>((resolve) => {
        canvas.toBlob(async (blob) => {
          if (blob) {
            await uploadBlobToS3(blob, plan);
          } else {
            console.error('Blobの生成に失敗しました');
          }
          resolve();
        }, 'image/jpeg', 0.9);
      });
    }
  };


  const addPhotoSet = () => {
    if (!data || !data.photo_sets) return;
    const newPhotoSet = {
      id: Date.now(),
      title: "",
      before_image_path: null,
      before_comment: "",
      middle_image_path: null,
      middle_comment: "",
      after_image_path: null,
      after_comment: ""
    };
    setData({ ...data, photo_sets: [...data.photo_sets, newPhotoSet] });
  };


  // Blobをアップロードする共通関数（createFallbackCaptureから呼び出される）
  const uploadBlobToS3 = async (blob: Blob, plan: any) => {
    // S3アップロードURLを取得
    const uploadUrlResponse = await getImageUploadUrl({
      filename: `floor_plan_${plan.id || Date.now()}.jpg`,
      folder: 'floor_plans'
    });

    if (uploadUrlResponse?.upload_url) {
      const { url, fields } = uploadUrlResponse.upload_url;

      // FormDataの作成
      const formData = new FormData();
      // 認証フィールドを追加
      Object.entries(fields).forEach(([key, value]) => {
        formData.append(key, value as string);
      });
      // Blobを追加
      formData.append('file', blob);

      // S3へのアップロード
      const response = await fetch(url, {
        method: 'POST',
        body: formData
      });

      if (response.ok) {
        // アップロード成功後のURLを構築
        const newUrl = `${url}${fields.key}`;
        plan.floor_plan_image_url = newUrl;
      } else {
        console.error('Floor plan image upload failed:', response.status, response.statusText);
        const errorText = await response.text();
        console.error('Error details:', errorText);
      }
    }
  };



  const submit = async () => {
    if (!data || !idNum) return;

    // 「月不明」を選んだのに製造年が4桁揃っていない場合だけ保存を止める。
    // 旧アプリの製造番号など書式外の既存値は対象外（値を壊さないため）。
    const monthUnknownErrors: string[] = [];
    (data.indoor_units_list ?? []).forEach((unit, index) => {
      if (validateMonthUnknownInput(String(unit.serial_number || ''))) {
        monthUnknownErrors.push(`内機 ${index + 1} の製造年月: 製造年を4桁で入力してください`);
      }
    });
    (data.outdoor_units_list ?? []).forEach((unit, index) => {
      if (validateMonthUnknownInput(String(unit.manufacturing_date || ''))) {
        monthUnknownErrors.push(`外機 ${index + 1} の製造年月: 製造年を4桁で入力してください`);
      }
    });
    if (monthUnknownErrors.length > 0) {
      alert(monthUnknownErrors.join("\n"));
      return;
    }

    setIsLoading(true);

    try {
      // 重要な問題回避策: データを直接操作する前にディープコピーを作成
      const workingData = JSON.parse(JSON.stringify(data));

      // 背景画像を読み込めなかった見取り図は、撮り直すと背景の無い画像で置き換わるため、
      // 編集前のまま残す（撮り直し・削除をしない。utils/floorPlanSave.ts）
      let keptFloorPlanIds = new Set<number>();

      // 1. 見取り図の処理を改善
      if (workingData.floor_plans) {
        keptFloorPlanIds = keepFloorPlansWithBackgroundLoadError(
          workingData.floor_plans,
          originalData?.floor_plans,
          floorPlanEditorRefs.current
        );

        // 1-A: 線の確定処理 - 各エディターに線の確定を要求
        const confirmPromises = [];

        for (let i = 0; i < workingData.floor_plans.length; i++) {
          const plan = workingData.floor_plans[i];
          if (keptFloorPlanIds.has(plan.id)) continue;

          const editorRef = floorPlanEditorRefs.current[plan.id];
          if (editorRef && editorRef.confirmCurrentLine) {
            try {
              const confirmPromise = editorRef.confirmCurrentLine()
                .then(() => {
                  return true;
                })
                .catch((error: any) => {
                  console.error(`見取り図 ${i + 1} (ID: ${plan.id}) の線確定処理でエラーが発生しました:`, error);
                  return false;
                });

              confirmPromises.push(confirmPromise);
            } catch (error) {
              console.error(`見取り図 ${i + 1} (ID: ${plan.id}) の線確定メソッド呼び出しでエラーが発生しました:`, error);
            }
          }
        }

        if (confirmPromises.length > 0) {
          await Promise.all(confirmPromises);
          await new Promise(resolve => setTimeout(resolve, 500));
        }

        // 1-B: JSONデータ取得 - 各エディターから最新のJSONデータを取得
        for (let i = 0; i < workingData.floor_plans.length; i++) {
          const plan = workingData.floor_plans[i];
          if (keptFloorPlanIds.has(plan.id)) continue;

          const editorRef = floorPlanEditorRefs.current[plan.id];
          if (editorRef && editorRef.getCurrentJsonData) {
            try {
              const currentJsonData = editorRef.getCurrentJsonData();
              if (currentJsonData) {
                const oldLength = plan.json_data?.length || 0;
                plan.json_data = currentJsonData;

                // 元のdataオブジェクトのfloor_plansも更新
                if (data.floor_plans && data.floor_plans[i]) {
                  data.floor_plans[i].json_data = currentJsonData;
                }
              }
            } catch (error) {
              console.error(`見取り図 ${i + 1} (ID: ${plan.id}) のJSONデータの取得でエラーが発生しました:`, error);
            }
          }

          // 各見取り図の処理間に少し間隔を開ける
          await new Promise(resolve => setTimeout(resolve, 100));
        }


        // 1-C: キャプチャ処理 - 各エディターのキャンバスをキャプチャしてアップロード
        for (let i = 0; i < workingData.floor_plans.length; i++) {
          const plan = workingData.floor_plans[i];
          if (keptFloorPlanIds.has(plan.id)) continue;

          // 旧形式のデータでなければキャプチャを更新
          const isLegacyData = Boolean(
            plan.image_data &&
            !plan.background_image_url &&
            !plan.floor_plan_image_url
          );

          if (!isLegacyData) {
            // 既存のキャプチャURLがあれば削除
            if (plan.floor_plan_image_url && plan.floor_plan_image_url.startsWith('https://')) {
              try {
                await deleteFile({ fileUrl: plan.floor_plan_image_url });
              } catch (deleteError) {
                console.error(`見取り図 ${i + 1} (ID: ${plan.id}) の既存の画像削除に失敗:`, deleteError);
              }
            }

            // キャプチャ処理
            const editorRef = floorPlanEditorRefs.current[plan.id];
            if (editorRef && editorRef.captureCanvas) {
              try {
                await new Promise(resolve => setTimeout(resolve, 300));

                const capturedBlob = await editorRef.captureCanvas();

                if (capturedBlob) {

                  // S3アップロード
                  const uploadUrlResponse = await getImageUploadUrl({
                    filename: `floor_plan_${plan.id || Date.now()}.jpg`,
                    folder: 'floor_plans'
                  });

                  if (uploadUrlResponse?.upload_url) {
                    const { url, fields } = uploadUrlResponse.upload_url;

                    const formData = new FormData();
                    Object.entries(fields).forEach(([key, value]) => {
                      formData.append(key, value as string);
                    });
                    formData.append('file', capturedBlob);

                    const response = await fetch(url, {
                      method: 'POST',
                      body: formData
                    });

                    if (response.ok) {
                      const newUrl = `${url}${fields.key}`;
                      plan.floor_plan_image_url = newUrl;

                      // 元のdataオブジェクトのfloor_plansも更新
                      if (data.floor_plans && data.floor_plans[i]) {
                        data.floor_plans[i].floor_plan_image_url = newUrl;
                      }
                    } else {
                      console.error(`見取り図 ${i + 1} (ID: ${plan.id}) のアップロードに失敗:`, response.status, response.statusText);
                      await createFallbackCapture(plan);
                    }
                  }
                } else {
                  console.error(`見取り図 ${i + 1} (ID: ${plan.id}) のキャプチャに失敗しました`);
                  await createFallbackCapture(plan);
                }
              } catch (error) {
                console.error(`見取り図 ${i + 1} (ID: ${plan.id}) のキャプチャ処理でエラー:`, error);
                await createFallbackCapture(plan);
              }
            } else {
              console.warn(`見取り図 ${i + 1} (ID: ${plan.id}) のエディタ参照がありません（キャプチャ）`);
              await createFallbackCapture(plan);
            }
          }
        }
      }

      // 2. 写真の更新処理
      let processedPhotos = 0;
      for (const [photoSetId, changes] of Array.from(photoChanges.entries())) {
        processedPhotos++;

        // Before画像の処理
        if (changes.before?.newFile) {

          // 古い画像の削除
          if (changes.before.oldUrl) {
            try {
              await deleteFile({ fileUrl: changes.before.oldUrl });
            } catch (error) {
              console.error(`写真 ${processedPhotos} (ID: ${photoSetId}) の古いBefore画像削除に失敗:`, error);
            }
          }

          try {
            changes.before.newUrl = await uploadPhoto(changes.before);
          } catch (error) {
            console.error(`写真 ${processedPhotos} (ID: ${photoSetId}) のBefore画像アップロードに失敗:`, error);
          }
        }

        // After画像の処理
        if (changes.after?.newFile) {

          // 古い画像の削除
          if (changes.after.oldUrl) {
            try {
              await deleteFile({ fileUrl: changes.after.oldUrl });
            } catch (error) {
              console.error(`写真 ${processedPhotos} (ID: ${photoSetId}) の古いAfter画像削除に失敗:`, error);
            }
          }

          try {
            changes.after.newUrl = await uploadPhoto(changes.after);
          } catch (error) {
            console.error(`写真 ${processedPhotos} (ID: ${photoSetId}) のAfter画像アップロードに失敗:`, error);
          }
        }

        // Middle画像の処理
        if (changes.middle?.newFile) {

          // 古い画像の削除
          if (changes.middle.oldUrl) {
            try {
              await deleteFile({ fileUrl: changes.middle.oldUrl });
            } catch (error) {
              console.error(`写真 ${processedPhotos} (ID: ${photoSetId}) の古いMiddle画像削除に失敗:`, error);
            }
          }

          try {
            changes.middle.newUrl = await uploadPhoto(changes.middle);
          } catch (error) {
            console.error(`写真 ${processedPhotos} (ID: ${photoSetId}) のMiddle画像アップロードに失敗:`, error);
          }
        }
      }


      // 3. リクエストデータの構築
      const updateData: AdminUpdateReportAdminReportReportIdPutData = {
        reportId: idNum,
        requestBody: {
          delivery_destination: workingData.delivery_destination || '',
          address1: workingData.address1 || '',
          start_date: workingData.start_date || new Date().toISOString(),
          end_date: workingData.end_date || new Date().toISOString(),
          sales_person: workingData.sales_person || '',
          sales_person_id: workingData.sales_person_id || null,
          indoor_units: workingData.indoor_units || 0,
          outdoor_units: workingData.outdoor_units || 0,
          indoor_rooms: workingData.indoor_rooms || 0,
          outdoor_rooms: workingData.outdoor_rooms || 0,
          responsible_person: workingData.responsible_person || '',
          memo: workingData.memo || '',
          worker_memo: workingData.worker_memo || '',
          points_to_note: workingData.points_to_note || '',
          work_result: workingData.work_result === "継続" ? "継続" : "完了",
          status: workingData.status || "未対応",
          sales_status: workingData.sales_status || "未対応",
          signature: workingData.signature || null,

          // 作業者情報
          workers: workingData.workers?.map((worker: { worker_name: any; company_name: any; }) => ({
            worker_name: worker.worker_name || '',
            company_name: worker.company_name || '',
          })) || [],

          // 内機情報
          indoor_units_list: workingData.indoor_units_list?.map((unit: { management_no: any; manufacturer: any; manufacturer_id?: any; type: any; model_number: any; serial_number: any; panel_model_number: any; air_volume: any; pre_operation_check: any; pre_damage_check: any; work_drain_pump: any; work_hose: any; work_heat_exchanger: any; work_drain_pan: any; work_grill: any; work_filter: any; work_fan: any; post_drainage: any; post_noise_vibration: any; post_operation: any; measurement_mode: any; temp_before_suction: any; temp_before_discharge: any; temp_after_suction: any; temp_after_discharge: any; wind_before_suction: any; wind_before_discharge: any; wind_after_suction: any; wind_after_discharge: any; assembler: any; photo_sets: PhotoSetResponse[] | null | undefined; }) => ({
            management_no: unit.management_no || '',
            manufacturer: unit.manufacturer || '',
            manufacturer_id: unit.manufacturer_id || null,
            type: unit.type || '',
            model_number: unit.model_number || '',
            serial_number: unit.serial_number || '',
            panel_model_number: unit.panel_model_number || '',
            air_volume: unit.air_volume || '',
            pre_operation_check: Boolean(unit.pre_operation_check),
            pre_damage_check: Boolean(unit.pre_damage_check),
            work_drain_pump: unit.work_drain_pump || WORK_LOCATION_CHECK_OPTIONS.NONE,
            work_hose: unit.work_hose || WORK_LOCATION_CHECK_OPTIONS.NONE,
            work_heat_exchanger: unit.work_heat_exchanger || WORK_LOCATION_CHECK_OPTIONS.NONE,
            work_drain_pan: unit.work_drain_pan || WORK_LOCATION_CHECK_OPTIONS.NONE,
            work_grill: unit.work_grill || WORK_LOCATION_CHECK_OPTIONS.NONE,
            work_filter: unit.work_filter || WORK_LOCATION_CHECK_OPTIONS.NONE,
            work_fan: unit.work_fan || WORK_LOCATION_CHECK_OPTIONS.NONE,
            post_drainage: unit.post_drainage || '',
            post_noise_vibration: unit.post_noise_vibration || '',
            post_operation: unit.post_operation || '',
            measurement_mode: unit.measurement_mode || '',
            temp_before_suction: Number(unit.temp_before_suction) || 0,
            temp_before_discharge: Number(unit.temp_before_discharge) || 0,
            temp_after_suction: Number(unit.temp_after_suction) || 0,
            temp_after_discharge: Number(unit.temp_after_discharge) || 0,
            wind_before_suction: Number(unit.wind_before_suction) || 0,
            wind_before_discharge: Number(unit.wind_before_discharge) || 0,
            wind_after_suction: Number(unit.wind_after_suction) || 0,
            wind_after_discharge: Number(unit.wind_after_discharge) || 0,
            assembler: unit.assembler || '',
            photo_sets: getUpdatedPhotoSets(unit.photo_sets),
          })) || [],

          // 外機情報
          outdoor_units_list: workingData.outdoor_units_list?.map((unit: { management_no: any; manufacturer: any; manufacturer_id?: any; corresponding_indoor_units: any; model_number: any; serial_number: any; refrigerant_type: any; manufacturing_date: any; pre_noise_vibration: any; pre_damage_dent: any; pre_heat_exchanger: any; pre_external_cleaning: any; post_noise_vibration: any; photo_sets: PhotoSetResponse[] | null | undefined; }) => ({
            management_no: unit.management_no || '',
            manufacturer: unit.manufacturer || '',
            manufacturer_id: unit.manufacturer_id || null,
            corresponding_indoor_units: Array.isArray(unit.corresponding_indoor_units)
              ? unit.corresponding_indoor_units
              : (unit.corresponding_indoor_units ? [unit.corresponding_indoor_units] : []),
            model_number: unit.model_number || '',
            serial_number: unit.serial_number || '',
            refrigerant_type: unit.refrigerant_type || '',
            manufacturing_date: unit.manufacturing_date || '',
            pre_noise_vibration: unit.pre_noise_vibration || WORK_CHECK_OPTIONS.NONE,
            pre_damage_dent: unit.pre_damage_dent || WORK_CHECK_OPTIONS.NONE,
            pre_heat_exchanger: unit.pre_heat_exchanger || WORK_CHECK_OPTIONS.NONE,
            pre_external_cleaning: unit.pre_external_cleaning || WORK_CHECK_OPTIONS.NONE,
            post_noise_vibration: unit.post_noise_vibration || WORK_CHECK_OPTIONS.NONE,
            photo_sets: getUpdatedPhotoSets(unit.photo_sets),
          })) || [],

          // その他作業情報
          other_works: workingData.other_works?.map((work: { work_type: any; work_type_id?: any; details: any; quantity: any; unit: any; photo_sets: PhotoSetResponse[] | null | undefined; }) => ({
            work_type: work.work_type || '',
            work_type_id: work.work_type_id || null,
            details: work.details || '',
            quantity: work.quantity || null,
            unit: work.unit || null,
            photo_sets: getUpdatedPhotoSets(work.photo_sets),
          })) || [],


          // 見取り図情報 - image_dataを更新せず、元のデータを維持
          floor_plans: workingData.floor_plans?.map((plan: { id: number; background_image_url: any; floor_plan_image_url: any; title: any; json_data: any; }) => {
            // 元のimage_dataを取得（または空文字列）
            const originalImageData = originalData?.floor_plans?.find(p => p.id === plan.id)?.image_data || '';

            // 旧形式のデータかどうかを判定
            const isLegacyData = Boolean(
              originalImageData &&
              !plan.background_image_url &&
              !plan.floor_plan_image_url
            );

            // 旧形式のデータの場合は、floor_plan_image_urlを含めない
            if (isLegacyData) {
              return {
                title: plan.title || '',
                json_data: plan.json_data || '',
                image_data: originalImageData,  // 元のimage_dataを使用
                background_image_url: null
                // floor_plan_image_urlは含めない
              };
            }

            // 通常の新形式データの場合
            return {
              title: plan.title || '',
              json_data: plan.json_data || '',
              image_data: originalImageData,  // 元のimage_dataを使用
              background_image_url: plan.background_image_url || null,
              floor_plan_image_url: plan.floor_plan_image_url || null
            };
          }) || [],

          // 写真セット情報
          photo_sets: getUpdatedPhotoSets(workingData.photo_sets),
        }
      };

      // 4. データ更新API呼び出し
      const response = await updateReport(updateData);

      if (response) {
        setPhotoChanges(new Map());
        setIsEditing(false);
        await getData(); // 最新データを再取得
        if (keptFloorPlanIds.size > 0) {
          alert(KEPT_FLOOR_PLAN_MESSAGE);
        }
      } else {
        console.error("API更新リクエストが失敗しました");
        alert("更新に失敗しました。再度お試しください。");
      }
    } catch (error) {
      console.error("Failed to update report", error);
      alert("更新に失敗しました。再度お試しください。");
    } finally {
      setIsLoading(false); // 処理完了時にローディングを非表示
    }
  };

  const addWorker = () => {
    if (!data || !data.workers) return;
    const newWorker = {
      id: Date.now(), // temporary ID
      worker_name: "",
      company_name: "",
      start_date: new Date().toISOString(),
      end_date: new Date().toISOString()
    };
    setData({ ...data, workers: [...data.workers, newWorker] });
  };

  const addIndoorUnit = () => {
    if (!data || !data.indoor_units_list) return;
    const newUnit = {
      id: Date.now(),
      management_no: "",
      manufacturer: "",
      type: "",
      model_number: "",
      serial_number: "",
      panel_model_number: "",
      air_volume: "",
      pre_operation_check: false,
      pre_damage_check: false,
      work_drain_pump: WORK_LOCATION_CHECK_OPTIONS.NONE,
      work_hose: WORK_LOCATION_CHECK_OPTIONS.NONE,
      work_heat_exchanger: WORK_LOCATION_CHECK_OPTIONS.NONE,
      work_drain_pan: WORK_LOCATION_CHECK_OPTIONS.NONE,
      work_grill: WORK_LOCATION_CHECK_OPTIONS.NONE,
      work_filter: WORK_LOCATION_CHECK_OPTIONS.NONE,
      work_fan: WORK_LOCATION_CHECK_OPTIONS.NONE,
      measurement_mode: "",
      temp_before_suction: 0,
      temp_before_discharge: 0,
      temp_before_diff: 0,
      temp_after_suction: 0,
      temp_after_discharge: 0,
      temp_after_diff: 0,
      wind_before_suction: 0,
      wind_before_discharge: 0,
      wind_before_diff: 0,
      wind_after_suction: 0,
      wind_after_discharge: 0,
      wind_after_diff: 0,
      assembler: "",
    };
    setData({ ...data, indoor_units_list: [...data.indoor_units_list, newUnit] });
  };

  const addOutdoorUnit = () => {
    if (!data || !data.outdoor_units_list) return;
    const newUnit = {
      id: Date.now(),
      management_no: "",
      manufacturer: "",
      corresponding_indoor_units: [],
      model_number: "",
      serial_number: "",
      refrigerant_type: "",
      manufacturing_date: "",
      pre_noise_vibration: WORK_CHECK_OPTIONS.NONE,
      pre_damage_dent: WORK_CHECK_OPTIONS.NONE,
      pre_heat_exchanger: WORK_CHECK_OPTIONS.NONE,
      pre_external_cleaning: WORK_CHECK_OPTIONS.NONE,
      post_noise_vibration: WORK_CHECK_OPTIONS.NONE
    };
    setData({ ...data, outdoor_units_list: [...data.outdoor_units_list, newUnit] });
  };



  const addOtherWork = () => {
    if (!data || !data.other_works) return;
    const newWork = {
      id: Date.now(),
      work_type: "",
      details: "",
      quantity: null,
      unit: null,
    };
    setData({ ...data, other_works: [...data.other_works, newWork] });
  };


  const getIndoorUnitManagementNumbers = () => {
    if (!data?.indoor_units_list) return [];
    return data.indoor_units_list
      .map(unit => unit.management_no)
      .filter(no => no && no.trim() !== '') // 空の値をフィルタリング
      .sort();
  };


  const renderEditableField = (
    value: any,
    onChange: (value: any) => void,
    type: 'text' | 'number' | 'array' = 'text'
  ) => {
    const validValue = isValidFieldValue(value) ? value : undefined;

    if (isEditing) {
      if (type === 'array') {
        const indoorUnitNumbers = getIndoorUnitManagementNumbers();
        return (
          <FormControl fullWidth size="small">
            <Select
              multiple
              value={Array.isArray(value) ? value : []}
              onChange={(e) => {
                const selectedValues = typeof e.target.value === 'string'
                  ? e.target.value.split(',')
                  : e.target.value;
                onChange(selectedValues);
              }}
              input={<OutlinedInput />}
              renderValue={(selected) => (
                Array.isArray(selected) ? selected.join(', ') : ''
              )}
              MenuProps={MenuProps}
            >
              {indoorUnitNumbers.map((number) => (
                <MenuItem key={number} value={number as string}>
                  <Checkbox checked={Array.isArray(value) && value.includes(number)} />
                  <ListItemText primary={number} />
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        );
      }
      return (
        <TextField
          fullWidth
          value={validValue?.toString() || ''}
          onChange={(e) => onChange(e.target.value)}
          size="small"
          type={type}
        />
      );
    }
    return Array.isArray(value) ? value.join(', ') : (validValue?.toString() || '');
  };


  // renderPhotoSetContent関数の修正版
  const renderPhotoSetContent = (
    photoSet: PhotoSetResponse,
    onChange: (updatedPhotoSet: PhotoSetResponse) => void
  ) => {
    // 画像のアスペクト比を設定 (4:3)
    const imageAspectRatio = 4 / 3; // ≈ 1.33
    // 画像表示の統一サイズ設定
    const imageDisplayHeight = 240; // 高さを固定値に設定（ピクセル）

    return (
      <Box>
        <Grid container spacing={2}>
          {/* Before写真エリア */}
          <Grid item xs={12} md={6}>
            <Box>
              <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                作業前
              </Typography>
              <Box mb={2}>
                <FixedAspectImage
                  imageUrl={photoSet.before_image_path}
                  alt="Before"
                  aspectRatio={imageAspectRatio}
                  height={imageDisplayHeight}
                />
              </Box>
              {isEditing && (
                <Box mb={2}>
                  <ImageSelector
                    existingImageUrl={photoSet.before_image_path || null}
                    onImageSelected={(file, imageUrl) => {
                      handlePhotoChange(photoSet, 'before', file, onChange);
                      // 一時的に表示用URLを設定
                      onChange({
                        ...photoSet,
                        before_image_path: imageUrl
                      });
                    }}
                    onDeleteImage={() => {
                      cleanupBlobUrl(photoSet.before_image_path);
                      clearPhotoChange(photoSet.id, 'before');
                      onChange({
                        ...photoSet,
                        before_image_path: null
                      });
                    }}
                    aspectRatio={imageAspectRatio}
                    buttonLabel="写真を選択"
                  />
                </Box>
              )}
            </Box>
          </Grid>

          {/* After写真エリア */}
          <Grid item xs={12} md={6}>
            <Box>
              <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                作業後
              </Typography>
              <Box mb={2}>
                <FixedAspectImage
                  imageUrl={photoSet.after_image_path}
                  alt="After"
                  aspectRatio={imageAspectRatio}
                  height={imageDisplayHeight}
                />
              </Box>
              {isEditing && (
                <Box mb={2}>
                  <ImageSelector
                    existingImageUrl={photoSet.after_image_path || null}
                    onImageSelected={(file, imageUrl) => {
                      handlePhotoChange(photoSet, 'after', file, onChange);
                      // 一時的に表示用URLを設定
                      onChange({
                        ...photoSet,
                        after_image_path: imageUrl
                      });
                    }}
                    onDeleteImage={() => {
                      cleanupBlobUrl(photoSet.after_image_path);
                      clearPhotoChange(photoSet.id, 'after');
                      onChange({
                        ...photoSet,
                        after_image_path: null
                      });
                    }}
                    aspectRatio={imageAspectRatio}
                    buttonLabel="写真を選択"
                  />
                </Box>
              )}
            </Box>
          </Grid>
        </Grid>
      </Box>
    );
  };


  const renderPhotoSetAnoterContent = (
    photoSet: PhotoSetResponse,
    onChange: (updatedPhotoSet: PhotoSetResponse) => void
  ) => {
    // 画像のアスペクト比を設定 (4:3)
    const imageAspectRatio = 4 / 3; // ≈ 1.33
    // 画像表示の統一サイズ設定
    const imageDisplayHeight = 240; // 高さを固定値に設定（ピクセル）

    return (
      <Box>
        <Grid container spacing={2}>
          {/* Before写真エリア */}
          <Grid item xs={12} md={4}>
            <Box>
              <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                作業前
              </Typography>
              <Box mb={2}>
                <FixedAspectImage
                  imageUrl={photoSet.before_image_path}
                  alt="Before"
                  aspectRatio={imageAspectRatio}
                  height={imageDisplayHeight}
                />
              </Box>
              {isEditing && (
                <Box mb={2}>
                  <ImageSelector
                    existingImageUrl={photoSet.before_image_path || null}
                    onImageSelected={(file, imageUrl) => {
                      handlePhotoChange(photoSet, 'before', file, onChange);
                      // 一時的に表示用URLを設定
                      onChange({
                        ...photoSet,
                        before_image_path: imageUrl
                      });
                    }}
                    onDeleteImage={() => {
                      cleanupBlobUrl(photoSet.before_image_path);
                      clearPhotoChange(photoSet.id, 'before');
                      onChange({
                        ...photoSet,
                        before_image_path: null
                      });
                    }}
                    aspectRatio={imageAspectRatio}
                    buttonLabel="写真を選択"
                  />
                </Box>
              )}

            </Box>
          </Grid>

          {/* Middle写真エリア */}
          <Grid item xs={12} md={4}>
            <Box>
              <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                作業中
              </Typography>
              <Box mb={2}>
                <FixedAspectImage
                  imageUrl={photoSet.middle_image_path}
                  alt="Middle"
                  aspectRatio={imageAspectRatio}
                  height={imageDisplayHeight}
                />
              </Box>
              {isEditing && (
                <Box mb={2}>
                  <ImageSelector
                    existingImageUrl={photoSet.middle_image_path || null}
                    onImageSelected={(file, imageUrl) => {
                      handlePhotoChange(photoSet, 'middle', file, onChange);
                      // 一時的に表示用URLを設定
                      onChange({
                        ...photoSet,
                        middle_image_path: imageUrl
                      });
                    }}
                    onDeleteImage={() => {
                      cleanupBlobUrl(photoSet.middle_image_path);
                      clearPhotoChange(photoSet.id, 'middle');
                      onChange({
                        ...photoSet,
                        middle_image_path: null
                      });
                    }}
                    aspectRatio={imageAspectRatio}
                    buttonLabel="写真を選択"
                  />
                </Box>
              )}
            </Box>
          </Grid>

          {/* After写真エリア */}
          <Grid item xs={12} md={4}>
            <Box>
              <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                作業後
              </Typography>
              <Box mb={2}>
                <FixedAspectImage
                  imageUrl={photoSet.after_image_path}
                  alt="After"
                  aspectRatio={imageAspectRatio}
                  height={imageDisplayHeight}
                />
              </Box>
              {isEditing && (
                <Box mb={2}>
                  <ImageSelector
                    existingImageUrl={photoSet.after_image_path || null}
                    onImageSelected={(file, imageUrl) => {
                      handlePhotoChange(photoSet, 'after', file, onChange);
                      // 一時的に表示用URLを設定
                      onChange({
                        ...photoSet,
                        after_image_path: imageUrl
                      });
                    }}
                    onDeleteImage={() => {
                      cleanupBlobUrl(photoSet.after_image_path);
                      clearPhotoChange(photoSet.id, 'after');
                      onChange({
                        ...photoSet,
                        after_image_path: null
                      });
                    }}
                    aspectRatio={imageAspectRatio}
                    buttonLabel="写真を選択"
                  />
                </Box>
              )}
            </Box>
          </Grid>
        </Grid>
      </Box>
    );
  };

  // 値を文字列に変換するユーティリティ関数
  const formatValue = (value: any): string => {
    if (value === null || value === undefined) {
      return '';
    }
    if (Array.isArray(value)) {
      return value.join(', ');
    }
    if (typeof value === 'object') {
      return JSON.stringify(value);
    }
    return String(value);
  };




  const renderSectionFooter = (onAdd: () => void, buttonText: string) => (
    isEditing && (
      <Box display="flex" justifyContent="flex-end" mt={2}>
        <Button
          variant="contained"
          color="primary"
          onClick={onAdd}
          startIcon={<Add />}
          size="small"
        >
          {buttonText}
        </Button>
      </Box>
    )
  );

  return (
    <PageContainer>
      <DeleteConfirmDialog />
      {isLoading && (
        <Box
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            bgcolor: 'rgba(255, 255, 255, 0.8)',
            zIndex: 9999,
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center'
          }}
        >
          <CircularProgress size={60} />
        </Box>
      )}

      {!data ? (
        <Grid container justifyContent="center" alignItems="center" sx={{ height: '100%' }}>
          <CircularProgress />
        </Grid>
      ) : (
        <>
          {/* ヘッダー */}
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="h5" component="div">エアコン報告書詳細</Typography>
            <Box display="flex" alignItems="center" gap={2}>
              {isEditing && (
                <>
                  <Button variant="outlined" onClick={handleCancel}>
                    キャンセル
                  </Button>
                  <Button variant="contained" color="primary" onClick={submit}>
                    保存
                  </Button>
                </>
              )}
              {!isEditing && (
                <>
                  <Box display="flex" alignItems="center" gap={2}>
                    {/* 営業ステータス - バッジスタイル */}
                    <FormControl size="small">
                      <InputLabel
                        sx={{
                          fontSize: '0.75rem',
                          transform: 'translate(14px, -9px) scale(0.75)',
                          bgcolor: 'background.paper',
                          px: 0.5
                        }}
                      >
                        営業ステータス
                      </InputLabel>
                      <Select
                        value={data?.sales_status}
                        onChange={async (e) => {
                          try {
                            await updateReportSalesStatus({
                              reportId: idNum,
                            });
                            setData(prevData =>
                              prevData ? {
                                ...prevData,
                                sales_status: e.target.value
                              } : null
                            );
                          } catch (error) {
                            console.error("営業ステータス更新に失敗しました:", error);
                            alert("営業ステータスの更新に失敗しました。再度お試しください。");
                          }
                        }}
                        sx={{
                          minWidth: 110,
                          '& .MuiSelect-select': {
                            color: data?.sales_status === "未対応" ? 'warning.main' : 'success.main',
                            fontWeight: 'bold',
                            fontSize: '0.875rem'
                          }
                        }}
                      >
                        <MenuItem value="未対応">未対応</MenuItem>
                        <MenuItem value="対応済み">対応済み</MenuItem>
                      </Select>
                    </FormControl>

                    {/* 報告書ステータス - バッジスタイル */}
                    <FormControl size="small">
                      <InputLabel
                        sx={{
                          fontSize: '0.75rem',
                          transform: 'translate(14px, -9px) scale(0.75)',
                          bgcolor: 'background.paper',
                          px: 0.5
                        }}
                      >
                        報告書ステータス
                      </InputLabel>
                      <Select
                        value={data?.status}
                        onChange={async (e) => {
                          try {
                            await updateReportStatus({
                              reportId: idNum,
                            });
                            setData(prevData =>
                              prevData ? {
                                ...prevData,
                                status: e.target.value
                              } : null
                            );
                          } catch (error) {
                            console.error("ステータス更新に失敗しました:", error);
                            alert("ステータスの更新に失敗しました。再度お試しください。");
                          }
                        }}
                        sx={{
                          minWidth: 110,
                          '& .MuiSelect-select': {
                            color: data?.status === "未対応" ? 'warning.main' : 'success.main',
                            fontWeight: 'bold',
                            fontSize: '0.875rem'
                          }
                        }}
                      >
                        <MenuItem value="未対応">未対応</MenuItem>
                        <MenuItem value="対応済み">対応済み</MenuItem>
                      </Select>
                    </FormControl>

                    {/* アクションボタン群 */}
                    <Box display="flex" gap={1}>
                      {data.pdf_url && (
                        <Button
                          startIcon={<FileUpload />}
                          variant="outlined"
                          onClick={handleDownloadDefaultPdf}
                          size="small"
                        >
                          現場提出時PDF出力
                        </Button>
                      )}
                      <Button
                        startIcon={<PictureAsPdf />}
                        variant="outlined"
                        onClick={handleDownloadPdf}
                        size="small"
                      >
                        PDF出力
                      </Button>
                      <Button
                        startIcon={<TableView />}
                        variant="outlined"
                        onClick={handleDownloadExcel}
                        size="small"
                      >
                        Excel出力
                      </Button>
                      <Button
                        variant="contained"
                        color="primary"
                        onClick={handleStartEditing}
                      >
                        編集
                      </Button>
                    </Box>
                  </Box>
                </>
              )}
            </Box>
          </Box>

          {/* 基本情報 */}
          <StyledAccordion defaultExpanded>
            <StyledAccordionSummary expandIcon={<ExpandMore />}>
              <Typography variant="h6">基本情報</Typography>
            </StyledAccordionSummary>
            <AccordionDetails>
              <Table>
                <TableBody>
                  {/* 通常の行 */}
                  <TableRow>
                    <StyledTableCell component="th" scope="row" className="item-label">
                      納入先
                    </StyledTableCell>
                    <TableCell>
                      {renderEditableField(
                        data.delivery_destination,
                        (value) => setData({ ...data, delivery_destination: value })
                      )}
                    </TableCell>
                  </TableRow>

                  {/* 住所の行 */}
                  <TableRow>
                    <StyledTableCell component="th" scope="row" className="item-label">
                      住所
                    </StyledTableCell>
                    <TableCell>
                      {renderEditableField(
                        data.address1,
                        (value) => setData({ ...data, address1: value })
                      )}
                    </TableCell>
                  </TableRow>
                  {/* 日時の行 */}
                  <TableRow>
                    <StyledTableCell component="th" scope="row" className="item-label">
                      作業日時
                    </StyledTableCell>
                    <TableCell>
                      <Box display="flex" gap={2}>
                        <Box flex={1}>
                          <Typography variant="body2" color="textSecondary">開始日時</Typography>
                          {renderEditableDate(
                            data.start_date,
                            (dateString) => setData({
                              ...data,
                              start_date: dateString
                            })
                          )}
                        </Box>
                        <Box flex={1}>
                          <Typography variant="body2" color="textSecondary">終了日時</Typography>
                          {renderEditableDate(
                            data.end_date,
                            (dateString) => setData({
                              ...data,
                              end_date: dateString
                            })
                          )}
                        </Box>
                      </Box>
                    </TableCell>
                  </TableRow>

                  {/* 台数の行 */}
                  <TableRow>
                    <StyledTableCell component="th" scope="row" className="item-label">
                      実施台数
                    </StyledTableCell>
                    <TableCell>
                      <Box display="flex" gap={2}>
                        <Box flex={1}>
                          <Typography variant="body2" color="textSecondary">内機台数</Typography>
                          {renderEditableField(
                            data.indoor_units,
                            (value) => setData({ ...data, indoor_units: Number(value) }),
                            'number'
                          )}
                        </Box>
                        <Box flex={1}>
                          <Typography variant="body2" color="textSecondary">外機台数</Typography>
                          {renderEditableField(
                            data.outdoor_units,
                            (value) => setData({ ...data, outdoor_units: Number(value) }),
                            'number'
                          )}
                        </Box>
                      </Box>
                    </TableCell>
                  </TableRow>

                  {/* ルーム台数の行 */}
                  <TableRow>
                    <StyledTableCell component="th" scope="row" className="item-label">
                      ルーム台数
                    </StyledTableCell>
                    <TableCell>
                      <Box display="flex" gap={2}>
                        <Box flex={1}>
                          <Typography variant="body2" color="textSecondary">内機ルーム台数</Typography>
                          {renderEditableField(
                            data.indoor_rooms,
                            (value) => setData({ ...data, indoor_rooms: Number(value) }),
                            'number'
                          )}
                        </Box>
                        <Box flex={1}>
                          <Typography variant="body2" color="textSecondary">外機ルーム台数</Typography>
                          {renderEditableField(
                            data.outdoor_rooms,
                            (value) => setData({ ...data, outdoor_rooms: Number(value) }),
                            'number'
                          )}
                        </Box>
                      </Box>
                    </TableCell>
                  </TableRow>

                  {/* その他の通常の行 */}
                  <TableRow>
                    <StyledTableCell component="th" scope="row" className="item-label">
                      作業責任者
                    </StyledTableCell>
                    <TableCell>
                      {renderEditableField(
                        data.responsible_person,
                        (value) => setData({ ...data, responsible_person: value })
                      )}
                    </TableCell>
                  </TableRow>

                  {/* 作業結果の行 */}
                  <TableRow>
                    <StyledTableCell component="th" scope="row" className="item-label">
                      作業結果
                    </StyledTableCell>
                    <TableCell>
                      {isEditing ? (
                        <TextField
                          select
                          value={data.work_result}
                          onChange={(e) => setData({ ...data, work_result: e.target.value })}
                          fullWidth
                          size="small"
                        >
                          <MenuItem value="完了">完了</MenuItem>
                          <MenuItem value="継続">継続</MenuItem>
                        </TextField>
                      ) : (
                        <Chip
                          label={data.work_result}
                          color={data.work_result === "完了" ? "success" : "warning"}
                        />
                      )}
                    </TableCell>
                  </TableRow>

                  <TableRow>
                    <StyledTableCell component="th" scope="row" className="item-label">
                      営業担当者
                    </StyledTableCell>
                    <TableCell>
                      {isEditing ? (
                        <Autocomplete
                          value={data.sales_person || ""}
                          inputValue={data.sales_person || ""}
                          onChange={(event, newValue) => {
                            // マスタデータから選択された場合はIDも設定
                            const selectedMaster = salesPersonOptionsWithId.find(option => option.name === newValue);
                            setData({ 
                              ...data, 
                              sales_person: newValue || "",
                              sales_person_id: selectedMaster ? selectedMaster.id : null
                            });
                          }}
                          onInputChange={(event, newInputValue) => {
                            // 自由入力の場合はIDをnullに設定
                            const selectedMaster = salesPersonOptionsWithId.find(option => option.name === newInputValue);
                            setData({ 
                              ...data, 
                              sales_person: newInputValue || "",
                              sales_person_id: selectedMaster ? selectedMaster.id : null
                            });
                          }}
                          options={salesPersonOptions}
                          loading={salesPersonLoading}
                          freeSolo
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              variant="outlined"
                              size="small"
                              InputProps={{
                                ...params.InputProps,
                                endAdornment: (
                                  <>
                                    {salesPersonLoading ? (
                                      <CircularProgress color="inherit" size={20} />
                                    ) : null}
                                    {params.InputProps.endAdornment}
                                  </>
                                ),
                              }}
                            />
                          )}
                        />
                      ) : (
                        data.sales_person
                      )}
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </AccordionDetails>
          </StyledAccordion>

          {/* 作業者情報 */}
          <StyledAccordion defaultExpanded>
            <StyledAccordionSummary expandIcon={<ExpandMore />}>
              <Typography variant="h6">作業者情報</Typography>
            </StyledAccordionSummary>
            <AccordionDetails>
              <Table>
                <TableBody>
                  {data.workers!.map((worker, index) => (
                    <React.Fragment key={worker.id}>
                      <TableRow>
                        <StyledTableCell colSpan={2} className="section-header">
                          <SectionHeader index={index} type="worker" />
                        </StyledTableCell>
                      </TableRow>
                      {/* 作業者名と会社名は変更なし */}
                      {[
                        { label: "作業者名", key: "worker_name" },
                        { label: "会社名", key: "company_name" },
                      ].map((item) => (
                        <TableRow key={item.key}>
                          <StyledTableCell component="th" scope="row" className="item-label">
                            {item.label}
                          </StyledTableCell>
                          <TableCell>
                            {renderEditableField(
                              worker[item.key as keyof typeof worker],
                              (value) => {
                                const newWorkers = [...data.workers!];
                                newWorkers[index] = { ...worker, [item.key]: value };
                                setData({ ...data, workers: newWorkers });
                              }
                            )}
                          </TableCell>
                        </TableRow>
                      ))}

                    </React.Fragment>
                  ))}
                </TableBody>
              </Table>
              {renderSectionFooter(addWorker, "作業者を追加")}
            </AccordionDetails>
          </StyledAccordion>

          {/* 内機情報 */}
          {data.indoor_units_list && (
            <StyledAccordion defaultExpanded>
              <StyledAccordionSummary expandIcon={<ExpandMore />}>
                <Typography variant="h6">内機情報</Typography>
              </StyledAccordionSummary>
              <AccordionDetails>
                {data.indoor_units_list.map((unit, index) => (
                  <StyledAccordion
                    key={unit.id}
                    expanded={expandedUnits[`indoor-${unit.id}`] ?? false}
                    onChange={handleUnitAccordionChange('indoor', unit.id)}
                    sx={{ mb: index < data.indoor_units_list!.length - 1 ? 2 : 0 }}
                  >
                    <StyledAccordionSummary expandIcon={<ExpandMore />}>
                      <SectionHeader index={index} type="indoor" />
                    </StyledAccordionSummary>
                    <AccordionDetails>
                      <Table>
                        <TableBody>
                          {/* 基本情報 */}
                          <TableRow>
                            <StyledTableCell colSpan={2} className="section-header">
                              基本情報
                            </StyledTableCell>
                          </TableRow>
                          {[
                            { label: "管理No", key: "management_no" },
                            { label: "メーカー", key: "manufacturer", type: "manufacturer", options: manufacturerOptions },
                            { label: "タイプ", key: "type", type: "type", options: TYPE_OPTIONS },
                            { label: "機器型式", key: "model_number" },
                            { label: "製造年月 (YYYYMM)", key: "serial_number" },
                            { label: "外装パネル型式", key: "panel_model_number" },
                          ].map((item) => (
                            <TableRow key={item.key}>
                              <StyledTableCell component="th" scope="row" className="item-label">
                                {item.label}
                              </StyledTableCell>
                              <TableCell>
                                {isEditing ? (
                                  item.options ? (
                                    <Autocomplete<string, false, false, true>
                                      freeSolo
                                      loading={item.type === 'manufacturer' ? manufacturerLoading : false}
                                      options={item.options}
                                      value={String(unit[item.key as keyof typeof unit] || '')}
                                      inputValue={String(unit[item.key as keyof typeof unit] || '')}
                                      onChange={(_, newValue) => {
                                        const newUnits = [...data.indoor_units_list!];
                                        if (item.key === 'manufacturer') {
                                          // 製造メーカーの場合はIDも設定
                                          const selectedMaster = manufacturerOptionsWithId.find(option => option.name === newValue);
                                          newUnits[index] = { 
                                            ...unit, 
                                            [item.key]: newValue || '', 
                                            manufacturer_id: selectedMaster ? selectedMaster.id : null
                                          };
                                        } else {
                                          newUnits[index] = { ...unit, [item.key]: newValue || '' };
                                        }
                                        setData({ ...data, indoor_units_list: newUnits });
                                      }}
                                      onInputChange={(_, newInputValue) => {
                                        const newUnits = [...data.indoor_units_list!];
                                        if (item.key === 'manufacturer') {
                                          // 製造メーカーの場合はIDも設定
                                          const selectedMaster = manufacturerOptionsWithId.find(option => option.name === newInputValue);
                                          newUnits[index] = { 
                                            ...unit, 
                                            [item.key]: newInputValue || '', 
                                            manufacturer_id: selectedMaster ? selectedMaster.id : null
                                          };
                                        } else {
                                          newUnits[index] = { ...unit, [item.key]: newInputValue || '' };
                                        }
                                        setData({ ...data, indoor_units_list: newUnits });
                                      }}
                                      renderInput={(params) => (
                                        <TextField
                                          {...params}
                                          fullWidth
                                          size="small"
                                        />
                                      )}
                                    />
                                  ) : item.key === 'serial_number' ? (
                                    (() => {
                                      const current = String(unit.serial_number || '');
                                      const isUnknown = current === '不明';
                                      const isUndetailed = current === '不詳';
                                      const isSpecial = isUnknown || isUndetailed;
                                      const monthUnknown = isMonthUnknown(current);
                                      const setValue = (v: string) => {
                                        const newUnits = [...data.indoor_units_list!];
                                        newUnits[index] = { ...unit, serial_number: v };
                                        setData({ ...data, indoor_units_list: newUnits });
                                      };
                                      return (
                                        <Box>
                                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <TextField
                                              fullWidth
                                              disabled={isSpecial}
                                              value={isSpecial ? '' : (monthUnknown ? yearPartOf(current) : current)}
                                              onChange={(e) => setValue(
                                                monthUnknown
                                                  ? buildMonthUnknown(e.target.value.replace(/[^0-9]/g, '').slice(0, 4))
                                                  : e.target.value
                                              )}
                                              size="small"
                                              error={validateMonthUnknownInput(current) !== null}
                                              placeholder={monthUnknown ? 'YYYY' : 'YYYYMM'}
                                              label={monthUnknown ? '製造年' : undefined}
                                            />
                                            <Box component="label" sx={{ display: 'flex', alignItems: 'center', whiteSpace: 'nowrap', cursor: 'pointer' }}>
                                              <Checkbox
                                                checked={monthUnknown}
                                                onChange={(e) => setValue(e.target.checked ? buildMonthUnknown(yearPartOf(current)) : '')}
                                              />
                                              月不明
                                            </Box>
                                            <Box component="label" sx={{ display: 'flex', alignItems: 'center', whiteSpace: 'nowrap', cursor: 'pointer' }}>
                                              <Checkbox checked={isUnknown} onChange={(e) => setValue(e.target.checked ? '不明' : '')} />
                                              不明
                                            </Box>
                                            <Box component="label" sx={{ display: 'flex', alignItems: 'center', whiteSpace: 'nowrap', cursor: 'pointer' }}>
                                              <Checkbox checked={isUndetailed} onChange={(e) => setValue(e.target.checked ? '不詳' : '')} />
                                              不詳
                                            </Box>
                                          </Box>
                                          {validateMonthUnknownInput(current) && (
                                            <FormHelperText error>
                                              {validateMonthUnknownInput(current)}
                                            </FormHelperText>
                                          )}
                                          {looksLikeUnknownMonthTypo(current) && (
                                            <FormHelperText>
                                              月が不明の場合は「月不明」にチェックしてください（現在の入力は月00として保存されます）
                                            </FormHelperText>
                                          )}
                                        </Box>
                                      );
                                    })()
                                  ) : (
                                    <TextField
                                      fullWidth
                                      value={String(unit[item.key as keyof typeof unit] || '')}
                                      onChange={(e) => {
                                        const newUnits = [...data.indoor_units_list!];
                                        newUnits[index] = { ...unit, [item.key]: e.target.value };
                                        setData({ ...data, indoor_units_list: newUnits });
                                      }}
                                      size="small"
                                    />
                                  )
                                ) : (
                                  <Typography>{String(unit[item.key as keyof typeof unit] || '')}</Typography>
                                )}
                              </TableCell>
                            </TableRow>
                          ))}

                          {/* 作業箇所 */}
                          {/* <TableRow>
                            <StyledTableCell colSpan={2} className="section-header">
                              作業箇所
                            </StyledTableCell>
                          </TableRow>
                          {[
                            { label: "ドレンポンプ", key: "work_drain_pump" },
                            { label: "ホース内", key: "work_hose" },
                            { label: "熱交換器", key: "work_heat_exchanger" },
                            { label: "ドレンパン", key: "work_drain_pan" },
                            { label: "グリル", key: "work_grill" },
                            { label: "フィルター", key: "work_filter" },
                            { label: "ファン", key: "work_fan" },
                          ].map((item) => (
                            <TableRow key={item.key}>
                              <StyledTableCell component="th" scope="row" className="item-label">
                                {item.label}
                              </StyledTableCell>
                              <TableCell>
                                <Box display="flex" gap={2}>
                                  {isEditing ? (
                                    <Box display="flex" gap={2}>
                                      <WorkCheckButton
                                        value="◯"
                                        isActive={unit[item.key as keyof typeof unit] === WORK_LOCATION_CHECK_OPTIONS.GOOD}
                                        onClick={() => {
                                          const newUnits = [...data.indoor_units_list!];
                                          newUnits[index] = { ...unit, [item.key]: WORK_LOCATION_CHECK_OPTIONS.GOOD };
                                          setData({ ...data, indoor_units_list: newUnits });
                                        }}
                                      />
                                      <WorkCheckButton
                                        value="△"
                                        isActive={unit[item.key as keyof typeof unit] === WORK_LOCATION_CHECK_OPTIONS.FAIR}
                                        onClick={() => {
                                          const newUnits = [...data.indoor_units_list!];
                                          newUnits[index] = { ...unit, [item.key]: WORK_LOCATION_CHECK_OPTIONS.FAIR };
                                          setData({ ...data, indoor_units_list: newUnits });
                                        }}
                                      />
                                      <WorkCheckButton
                                        value="ー"
                                        isActive={unit[item.key as keyof typeof unit] === WORK_LOCATION_CHECK_OPTIONS.NONE}
                                        onClick={() => {
                                          const newUnits = [...data.indoor_units_list!];
                                          newUnits[index] = { ...unit, [item.key]: WORK_LOCATION_CHECK_OPTIONS.NONE };
                                          setData({ ...data, indoor_units_list: newUnits });
                                        }}
                                      />
                                    </Box>
                                  ) : (
                                    <Typography>
                                      {formatValue(unit[item.key as keyof typeof unit]) || WORK_LOCATION_CHECK_OPTIONS.NONE}
                                    </Typography>
                                  )}
                                </Box>
                              </TableCell>
                            </TableRow>
                          ))} */}

                          {/* 風量 */}
                          <TableRow>
                            <StyledTableCell colSpan={2} className="section-header">
                              風量
                            </StyledTableCell>
                          </TableRow>
                          <TableRow>
                            <StyledTableCell component="th" scope="row" className="item-label">
                              急
                            </StyledTableCell>
                            <TableCell>
                              {isEditing ? (
                                <Checkbox
                                  checked={Boolean(unit.air_volume)}
                                  onChange={(e) => {
                                    const newUnits = [...data.indoor_units_list!];
                                    newUnits[index] = {
                                      ...unit,
                                      air_volume: e.target.checked ? "急" : ""
                                    };
                                    setData({ ...data, indoor_units_list: newUnits });
                                  }}
                                />
                              ) : (
                                // 非編集時は色を保持したまま操作できないように
                                <Checkbox
                                  checked={Boolean(unit.air_volume)}
                                  disabled
                                  disableRipple
                                  sx={{
                                    color: 'rgba(0, 0, 0, 0.6)', // 通常の色
                                    '&.Mui-disabled': {
                                      color: 'rgba(0, 0, 0, 0.6)' // 無効時も同じ色を維持
                                    }
                                  }}
                                />
                              )}
                            </TableCell>
                          </TableRow>

                          {/* 作業前 */}
                          <TableRow>
                            <StyledTableCell colSpan={2} className="section-header">
                              作業前
                            </StyledTableCell>
                          </TableRow>
                          {[
                            { label: "動作確認", key: "pre_operation_check" },
                            { label: "破損確認", key: "pre_damage_check" },
                          ].map((item) => (
                            <TableRow key={item.key}>
                              <StyledTableCell component="th" scope="row" className="item-label">
                                {item.label}
                              </StyledTableCell>
                              <TableCell>
                                {isEditing ? (
                                  <Checkbox
                                    checked={Boolean(unit[item.key as keyof typeof unit])}
                                    onChange={(e) => {
                                      const newUnits = [...data.indoor_units_list!];
                                      newUnits[index] = {
                                        ...unit,
                                        [item.key]: e.target.checked
                                      };
                                      setData({ ...data, indoor_units_list: newUnits });
                                    }}
                                  />
                                ) : (
                                  // 非編集時は色を保持したまま操作できないように
                                  <Checkbox
                                    checked={Boolean(unit[item.key as keyof typeof unit])}
                                    disabled
                                    disableRipple
                                    sx={{
                                      color: 'rgba(0, 0, 0, 0.6)', // 通常の色
                                      '&.Mui-disabled': {
                                        color: 'rgba(0, 0, 0, 0.6)' // 無効時も同じ色を維持
                                      }
                                    }}
                                  />
                                )}
                              </TableCell>
                            </TableRow>
                          ))}

                          {/* 作業後 */}
                          <TableRow>
                            <StyledTableCell colSpan={2} className="section-header">
                              作業後
                            </StyledTableCell>
                          </TableRow>
                          {[
                            { label: "排水確認", key: "post_drainage" },
                            { label: "異音・振動", key: "post_noise_vibration" },
                            { label: "動作確認", key: "post_operation" },
                          ].map((item) => (
                            <TableRow key={item.key}>
                              <StyledTableCell component="th" scope="row" className="item-label">
                                {item.label}
                              </StyledTableCell>
                              <TableCell>
                                <Box display="flex" gap={2}>
                                  {isEditing ? (
                                    <Box display="flex" gap={2}>
                                      <WorkCheckButton
                                        value="◯"
                                        isActive={unit[item.key as keyof typeof unit] === WORK_CHECK_OPTIONS.GOOD}
                                        onClick={() => {
                                          const newUnits = [...data.indoor_units_list!];
                                          newUnits[index] = { ...unit, [item.key]: WORK_CHECK_OPTIONS.GOOD };
                                          setData({ ...data, indoor_units_list: newUnits });
                                        }}
                                      />
                                      <WorkCheckButton
                                        value="△"
                                        isActive={unit[item.key as keyof typeof unit] === WORK_CHECK_OPTIONS.FAIR}
                                        onClick={() => {
                                          const newUnits = [...data.indoor_units_list!];
                                          newUnits[index] = { ...unit, [item.key]: WORK_CHECK_OPTIONS.FAIR };
                                          setData({ ...data, indoor_units_list: newUnits });
                                        }}
                                      />
                                      <WorkCheckButton
                                        value="×"
                                        isActive={unit[item.key as keyof typeof unit] === WORK_CHECK_OPTIONS.BAD}
                                        onClick={() => {
                                          const newUnits = [...data.indoor_units_list!];
                                          newUnits[index] = { ...unit, [item.key]: WORK_CHECK_OPTIONS.BAD };
                                          setData({ ...data, indoor_units_list: newUnits });
                                        }}
                                      />
                                      <WorkCheckButton
                                        value="ー"
                                        isActive={unit[item.key as keyof typeof unit] === WORK_CHECK_OPTIONS.NONE}
                                        onClick={() => {
                                          const newUnits = [...data.indoor_units_list!];
                                          newUnits[index] = { ...unit, [item.key]: WORK_CHECK_OPTIONS.NONE };
                                          setData({ ...data, indoor_units_list: newUnits });
                                        }}
                                      />
                                    </Box>
                                  ) : (
                                    <Typography>
                                      {formatValue(unit[item.key as keyof typeof unit]) || WORK_CHECK_OPTIONS.NONE}
                                    </Typography>
                                  )}
                                </Box>
                              </TableCell>
                            </TableRow>
                          ))}
                          {/* 測定モード */}
                          <TableRow>
                            <StyledTableCell component="th" scope="row" className="item-label">
                              測定モード
                            </StyledTableCell>
                            <TableCell>
                              {isEditing ? (
                                <TextField
                                  select
                                  value={unit.measurement_mode || ''}
                                  onChange={(e) => {
                                    const newUnits = [...data.indoor_units_list!];
                                    newUnits[index] = { ...unit, measurement_mode: e.target.value };
                                    setData({ ...data, indoor_units_list: newUnits });
                                  }}
                                  fullWidth
                                  size="small"
                                >
                                  <MenuItem value="暖">暖</MenuItem>
                                  <MenuItem value="冷">冷</MenuItem>
                                </TextField>
                              ) : (
                                <Typography>{unit.measurement_mode}</Typography>
                              )}
                            </TableCell>

                          </TableRow>

                          {/* 測定データ */}
                          <TableRow>
                            <StyledTableCell colSpan={2} className="section-header">
                              測定データ
                            </StyledTableCell>
                          </TableRow>
                          {/* 温度データ */}
                          <TableRow>
                            <StyledTableCell component="th" scope="row" className="item-label">
                              温度データ
                            </StyledTableCell>
                            <TableCell>
                              <Box display="grid" gridTemplateColumns="1fr 1fr" gap={2}>
                                {/* 作業前 */}
                                <Box>
                                  <Typography variant="body2" color="textSecondary" mb={1}>作業前</Typography>
                                  <Box display="grid" gap={1}>
                                    {[
                                      { label: "吸込温度", key: "temp_before_suction" },
                                      { label: "吹出温度", key: "temp_before_discharge" },
                                    ].map((item) => (
                                      <Box key={item.key} display="flex" alignItems="center">
                                        <Typography variant="caption" sx={{ width: '80px' }}>{item.label}</Typography>
                                        <Box display="flex" alignItems="center" flex={1}>
                                          {renderEditableField(
                                            unit[item.key as keyof typeof unit],
                                            (value) => {
                                              const newUnits = [...data.indoor_units_list!];
                                              newUnits[index] = { ...unit, [item.key]: value };
                                              setData({ ...data, indoor_units_list: newUnits });
                                            },
                                            'number'
                                          )}
                                          <Typography variant="body2" ml={1}>℃</Typography>
                                        </Box>
                                      </Box>
                                    ))}
                                    {/* 差分は絶対値で計算 */}
                                    <Box display="flex" alignItems="center">
                                      <Typography variant="caption" sx={{ width: '80px' }}>温度差</Typography>
                                      <Box display="flex" alignItems="center" flex={1}>
                                        <Typography>
                                          {Math.abs(Number(unit.temp_before_discharge) - Number(unit.temp_before_suction)).toFixed(1)}
                                        </Typography>
                                        <Typography variant="body2" ml={1}>℃</Typography>
                                      </Box>
                                    </Box>
                                  </Box>
                                </Box>

                                {/* 作業後 */}
                                <Box>
                                  <Typography variant="body2" color="textSecondary" mb={1}>作業後</Typography>
                                  <Box display="grid" gap={1}>
                                    {[
                                      { label: "吸込温度", key: "temp_after_suction" },
                                      { label: "吹出温度", key: "temp_after_discharge" },
                                    ].map((item) => (
                                      <Box key={item.key} display="flex" alignItems="center">
                                        <Typography variant="caption" sx={{ width: '80px' }}>{item.label}</Typography>
                                        <Box display="flex" alignItems="center" flex={1}>
                                          {renderEditableField(
                                            unit[item.key as keyof typeof unit],
                                            (value) => {
                                              const newUnits = [...data.indoor_units_list!];
                                              newUnits[index] = { ...unit, [item.key]: value };
                                              setData({ ...data, indoor_units_list: newUnits });
                                            },
                                            'number'
                                          )}
                                          <Typography variant="body2" ml={1}>℃</Typography>
                                        </Box>
                                      </Box>
                                    ))}
                                    {/* 差分は絶対値で計算 */}
                                    <Box display="flex" alignItems="center">
                                      <Typography variant="caption" sx={{ width: '80px' }}>温度差</Typography>
                                      <Box display="flex" alignItems="center" flex={1}>
                                        <Typography>
                                          {Math.abs(Number(unit.temp_after_discharge) - Number(unit.temp_after_suction)).toFixed(1)}
                                        </Typography>
                                        <Typography variant="body2" ml={1}>℃</Typography>
                                      </Box>
                                    </Box>
                                  </Box>
                                </Box>
                              </Box>
                            </TableCell>
                          </TableRow>

                          {/* 風速データ */}
                          <TableRow>
                            <StyledTableCell component="th" scope="row" className="item-label">
                              風速データ
                            </StyledTableCell>
                            <TableCell>
                              <Box display="grid" gridTemplateColumns="1fr 1fr" gap={2}>
                                {/* 吸込風速 */}
                                <Box>
                                  <Typography variant="body2" color="textSecondary" mb={1}>吸込風速</Typography>
                                  <Box display="grid" gap={1}>
                                    <Box display="flex" alignItems="center">
                                      <Typography variant="caption" sx={{ width: '80px' }}>作業前</Typography>
                                      <Box display="flex" alignItems="center" flex={1}>
                                        {renderEditableField(
                                          unit.wind_before_suction,
                                          (value) => {
                                            const newUnits = [...data.indoor_units_list!];
                                            newUnits[index] = { ...unit, wind_before_suction: value };
                                            setData({ ...data, indoor_units_list: newUnits });
                                          },
                                          'number'
                                        )}
                                        <Typography variant="body2" ml={1}>m/s</Typography>
                                      </Box>
                                    </Box>
                                    <Box display="flex" alignItems="center">
                                      <Typography variant="caption" sx={{ width: '80px' }}>作業後</Typography>
                                      <Box display="flex" alignItems="center" flex={1}>
                                        {renderEditableField(
                                          unit.wind_after_suction,
                                          (value) => {
                                            const newUnits = [...data.indoor_units_list!];
                                            newUnits[index] = { ...unit, wind_after_suction: value };
                                            setData({ ...data, indoor_units_list: newUnits });
                                          },
                                          'number'
                                        )}
                                        <Typography variant="body2" ml={1}>m/s</Typography>
                                      </Box>
                                    </Box>
                                    {/* 変化量を表示 */}
                                    <Box display="flex" alignItems="center">
                                      <Typography variant="caption" sx={{ width: '80px' }}>風速差</Typography>
                                      <Box display="flex" alignItems="center" flex={1}>
                                        <Typography>
                                          {Math.abs(Number(unit.wind_after_suction) - Number(unit.wind_before_suction)).toFixed(1)}
                                        </Typography>
                                        <Typography variant="body2" ml={1}>m/s</Typography>
                                      </Box>
                                    </Box>
                                  </Box>
                                </Box>

                                {/* 吹出風速 */}
                                <Box>
                                  <Typography variant="body2" color="textSecondary" mb={1}>吹出風速</Typography>
                                  <Box display="grid" gap={1}>
                                    <Box display="flex" alignItems="center">
                                      <Typography variant="caption" sx={{ width: '80px' }}>作業前</Typography>
                                      <Box display="flex" alignItems="center" flex={1}>
                                        {renderEditableField(
                                          unit.wind_before_discharge,
                                          (value) => {
                                            const newUnits = [...data.indoor_units_list!];
                                            newUnits[index] = { ...unit, wind_before_discharge: value };
                                            setData({ ...data, indoor_units_list: newUnits });
                                          },
                                          'number'
                                        )}
                                        <Typography variant="body2" ml={1}>m/s</Typography>
                                      </Box>
                                    </Box>
                                    <Box display="flex" alignItems="center">
                                      <Typography variant="caption" sx={{ width: '80px' }}>作業後</Typography>
                                      <Box display="flex" alignItems="center" flex={1}>
                                        {renderEditableField(
                                          unit.wind_after_discharge,
                                          (value) => {
                                            const newUnits = [...data.indoor_units_list!];
                                            newUnits[index] = { ...unit, wind_after_discharge: value };
                                            setData({ ...data, indoor_units_list: newUnits });
                                          },
                                          'number'
                                        )}
                                        <Typography variant="body2" ml={1}>m/s</Typography>
                                      </Box>
                                    </Box>
                                    {/* 変化量を表示 */}
                                    <Box display="flex" alignItems="center">
                                      <Typography variant="caption" sx={{ width: '80px' }}>風速差</Typography>
                                      <Box display="flex" alignItems="center" flex={1}>
                                        <Typography>
                                          {Math.abs(Number(unit.wind_after_discharge) - Number(unit.wind_before_discharge)).toFixed(1)}
                                        </Typography>
                                        <Typography variant="body2" ml={1}>m/s</Typography>
                                      </Box>
                                    </Box>
                                  </Box>
                                </Box>
                              </Box>
                            </TableCell>
                          </TableRow>

                          {/* 写真セクション - 基本情報の後に移動 */}
                          <TableRow>
                            <StyledTableCell colSpan={2} className="section-header">
                              写真
                            </StyledTableCell>
                          </TableRow>
                          <TableRow>
                            <TableCell colSpan={2}>
                              {unit.photo_sets?.map((photoSet, photoIndex) => (
                                <Box key={photoSet.id} mb={photoIndex < (unit.photo_sets?.length || 0) - 1 ? 4 : 0}>
                                  <Table>
                                    <TableBody>
                                      {/* 写真セットのヘッダー行に削除ボタンを追加 */}
                                      <TableRow>
                                        <StyledTableCell colSpan={2} className="section-header">
                                          <Box
                                            display="flex"
                                            justifyContent="space-between"
                                            alignItems="center"
                                            width="100%"
                                          >
                                            <Typography variant="subtitle1">
                                              写真セット {photoIndex + 1}
                                            </Typography>
                                            {isEditing && (
                                              <Button
                                                startIcon={<Delete />}
                                                color="error"
                                                size="small"
                                                onClick={(e) => {
                                                  e.stopPropagation();
                                                  handleDeleteClick('indoor_photo_set', photoIndex, index);
                                                }}
                                                sx={{ ml: 2 }}
                                              >
                                                削除
                                              </Button>
                                            )}
                                          </Box>
                                        </StyledTableCell>
                                      </TableRow>
                                      <TableRow>
                                        <StyledTableCell component="th" scope="row" className="item-label">
                                          タイトル
                                        </StyledTableCell>
                                        <TableCell>
                                          {isEditing ? (
                                            <TextField
                                              fullWidth
                                              value={photoSet.title || ''}
                                              onChange={(e) => {
                                                const newPhotoSets = [...(unit.photo_sets || [])];
                                                newPhotoSets[photoIndex] = {
                                                  ...photoSet,
                                                  title: e.target.value,
                                                };
                                                const newUnits = [...data.indoor_units_list!];
                                                newUnits[index] = { ...unit, photo_sets: newPhotoSets };
                                                setData({ ...data, indoor_units_list: newUnits });
                                              }}
                                              size="small"
                                            />
                                          ) : (
                                            <Typography>{photoSet.title || ''}</Typography>
                                          )}
                                        </TableCell>
                                      </TableRow>
                                      <TableRow>
                                        <TableCell colSpan={2}>
                                          {renderPhotoSetContent(photoSet, (updatedPhotoSet) => {
                                            const newPhotoSets = [...(unit.photo_sets || [])];
                                            newPhotoSets[photoIndex] = updatedPhotoSet;
                                            const newUnits = [...data.indoor_units_list!];
                                            newUnits[index] = { ...unit, photo_sets: newPhotoSets };
                                            setData({ ...data, indoor_units_list: newUnits });
                                          })}
                                        </TableCell>
                                      </TableRow>
                                    </TableBody>
                                  </Table>
                                </Box>
                              ))}
                              {isEditing && (
                                <Box display="flex" justifyContent="flex-end" mt={2}>
                                  <Button
                                    variant="contained"
                                    color="primary"
                                    onClick={() => {
                                      const newPhotoSet: IndoorUnitPhotoSetResponse = {
                                        id: Date.now(),
                                        title: "",
                                        before_image_path: null,
                                        before_comment: "",
                                        after_image_path: null,
                                        after_comment: "",
                                      };
                                      const newPhotoSets = [...(unit.photo_sets || []), newPhotoSet];
                                      const newUnits = [...data.indoor_units_list!];
                                      newUnits[index] = { ...unit, photo_sets: newPhotoSets };
                                      setData({ ...data, indoor_units_list: newUnits });
                                    }}
                                    startIcon={<Add />}
                                    size="small"
                                  >
                                    写真を追加
                                  </Button>
                                </Box>
                              )}
                            </TableCell>
                          </TableRow>

                        </TableBody>
                      </Table>
                    </AccordionDetails>
                  </StyledAccordion>
                ))}
                {renderSectionFooter(addIndoorUnit, "内機を追加")}
              </AccordionDetails>
            </StyledAccordion>
          )}


          {/* 外機情報 */}
          {data.outdoor_units_list && (
            <StyledAccordion defaultExpanded>
              <StyledAccordionSummary expandIcon={<ExpandMore />}>
                <Typography variant="h6">外機情報</Typography>
              </StyledAccordionSummary>
              <AccordionDetails>
                {data.outdoor_units_list.map((unit, index) => (
                  <StyledAccordion
                    key={unit.id}
                    expanded={expandedUnits[`outdoor-${unit.id}`] ?? false}
                    onChange={handleUnitAccordionChange('outdoor', unit.id)}
                    sx={{ mb: index < data.outdoor_units_list!.length - 1 ? 2 : 0 }}
                  >
                    <StyledAccordionSummary expandIcon={<ExpandMore />}>
                      <SectionHeader index={index} type="outdoor" />
                    </StyledAccordionSummary>
                    <AccordionDetails>
                      <Table>
                        <TableBody>
                          {/* 基本情報 */}
                          <TableRow>
                            <StyledTableCell colSpan={2} className="section-header">
                              基本情報
                            </StyledTableCell>
                          </TableRow>
                          {[
                            { label: "管理No", key: "management_no" },
                            { label: "メーカー", key: "manufacturer", type: "manufacturer", options: manufacturerOptions },
                            // { label: "対応内機", key: "corresponding_indoor_units", type: "array" },
                            { label: "機器型式", key: "model_number" },
                            { label: "製造番号", key: "serial_number" },
                            { label: "冷媒種類", key: "refrigerant_type", type: "refrigerant", options: REFRIGERANT_OPTIONS },
                            { label: "製造年月 (YYYYMM)", key: "manufacturing_date" },
                          ].map((item) => (
                            <TableRow key={item.key}>
                              <StyledTableCell component="th" scope="row" className="item-label">
                                {item.label}
                              </StyledTableCell>
                              <TableCell>
                                {item.key === "corresponding_indoor_units" ? (
                                  isEditing ? (
                                    <FormControl fullWidth size="small">
                                      <Select
                                        multiple
                                        value={Array.isArray(unit.corresponding_indoor_units) ? unit.corresponding_indoor_units : []}
                                        onChange={(e) => {
                                          const selectedValues = typeof e.target.value === 'string'
                                            ? e.target.value.split(',')
                                            : e.target.value;
                                          const newUnits = [...data.outdoor_units_list!];
                                          newUnits[index] = { ...unit, corresponding_indoor_units: selectedValues };
                                          setData({ ...data, outdoor_units_list: newUnits });
                                        }}
                                        input={<OutlinedInput />}
                                        renderValue={(selected) => (
                                          Array.isArray(selected) ? selected.join(', ') : ''
                                        )}
                                        MenuProps={MenuProps}
                                        displayEmpty
                                      >
                                        {getIndoorUnitManagementNumbers().map((number) => (
                                          <MenuItem key={number} value={number as string}>
                                            <Checkbox
                                              checked={
                                                Array.isArray(unit.corresponding_indoor_units) &&
                                                unit.corresponding_indoor_units.includes(number as string)
                                              }
                                            />
                                            <ListItemText primary={number} />
                                          </MenuItem>
                                        ))}
                                      </Select>
                                    </FormControl>
                                  ) : (
                                    <Typography>
                                      {Array.isArray(unit.corresponding_indoor_units)
                                        ? unit.corresponding_indoor_units.join(', ')
                                        : formatValue(unit.corresponding_indoor_units)}
                                    </Typography>
                                  )
                                ) : (
                                  // その他のフィールドの処理（元のコード）
                                  isEditing ? (
                                    item.options ? (
                                      <Autocomplete<string, false, false, true>
                                        freeSolo
                                        loading={item.type === 'manufacturer' ? manufacturerLoading : false}
                                        options={item.options}
                                        value={String(unit[item.key as keyof typeof unit] || '')}
                                        inputValue={String(unit[item.key as keyof typeof unit] || '')}
                                        onChange={(_, newValue) => {
                                          const newUnits = [...data.outdoor_units_list!];
                                          if (item.key === 'manufacturer') {
                                            // 製造メーカーの場合はIDも設定
                                            const selectedMaster = manufacturerOptionsWithId.find(option => option.name === newValue);
                                            newUnits[index] = { 
                                              ...unit, 
                                              [item.key]: newValue || '', 
                                              manufacturer_id: selectedMaster ? selectedMaster.id : null
                                            };
                                          } else {
                                            newUnits[index] = { ...unit, [item.key]: newValue || '' };
                                          }
                                          setData({ ...data, outdoor_units_list: newUnits });
                                        }}
                                        onInputChange={(_, newInputValue) => {
                                          const newUnits = [...data.outdoor_units_list!];
                                          if (item.key === 'manufacturer') {
                                            // 製造メーカーの場合はIDも設定
                                            const selectedMaster = manufacturerOptionsWithId.find(option => option.name === newInputValue);
                                            newUnits[index] = { 
                                              ...unit, 
                                              [item.key]: newInputValue || '', 
                                              manufacturer_id: selectedMaster ? selectedMaster.id : null
                                            };
                                          } else {
                                            newUnits[index] = { ...unit, [item.key]: newInputValue || '' };
                                          }
                                          setData({ ...data, outdoor_units_list: newUnits });
                                        }}
                                        renderInput={(params) => (
                                          <TextField
                                            {...params}
                                            fullWidth
                                            size="small"
                                            placeholder={item.key === "manufacturing_date" ? "YYYY/MM" : ""}
                                          />
                                        )}
                                      />
                                    ) : item.key === 'manufacturing_date' ? (
                                      (() => {
                                        const current = String(unit.manufacturing_date || '');
                                        const isUnknown = current === '不明';
                                        const isUndetailed = current === '不詳';
                                        const isSpecial = isUnknown || isUndetailed;
                                        const monthUnknown = isMonthUnknown(current);
                                        const setValue = (v: string) => {
                                          const newUnits = [...data.outdoor_units_list!];
                                          newUnits[index] = { ...unit, manufacturing_date: v };
                                          setData({ ...data, outdoor_units_list: newUnits });
                                        };
                                        return (
                                          <Box>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                              <TextField
                                                fullWidth
                                                disabled={isSpecial}
                                                value={isSpecial ? '' : (monthUnknown ? yearPartOf(current) : current)}
                                                onChange={(e) => setValue(
                                                  monthUnknown
                                                    ? buildMonthUnknown(e.target.value.replace(/[^0-9]/g, '').slice(0, 4))
                                                    : e.target.value
                                                )}
                                                size="small"
                                                error={validateMonthUnknownInput(current) !== null}
                                                placeholder={monthUnknown ? 'YYYY' : 'YYYYMM'}
                                                label={monthUnknown ? '製造年' : undefined}
                                              />
                                              <Box component="label" sx={{ display: 'flex', alignItems: 'center', whiteSpace: 'nowrap', cursor: 'pointer' }}>
                                                <Checkbox
                                                  checked={monthUnknown}
                                                  onChange={(e) => setValue(e.target.checked ? buildMonthUnknown(yearPartOf(current)) : '')}
                                                />
                                                月不明
                                              </Box>
                                              <Box component="label" sx={{ display: 'flex', alignItems: 'center', whiteSpace: 'nowrap', cursor: 'pointer' }}>
                                                <Checkbox checked={isUnknown} onChange={(e) => setValue(e.target.checked ? '不明' : '')} />
                                                不明
                                              </Box>
                                              <Box component="label" sx={{ display: 'flex', alignItems: 'center', whiteSpace: 'nowrap', cursor: 'pointer' }}>
                                                <Checkbox checked={isUndetailed} onChange={(e) => setValue(e.target.checked ? '不詳' : '')} />
                                                不詳
                                              </Box>
                                            </Box>
                                            {validateMonthUnknownInput(current) && (
                                              <FormHelperText error>
                                                {validateMonthUnknownInput(current)}
                                              </FormHelperText>
                                            )}
                                            {looksLikeUnknownMonthTypo(current) && (
                                              <FormHelperText>
                                                月が不明の場合は「月不明」にチェックしてください（現在の入力は月00として保存されます）
                                              </FormHelperText>
                                            )}
                                          </Box>
                                        );
                                      })()
                                    ) : (
                                      <TextField
                                        fullWidth
                                        value={String(unit[item.key as keyof typeof unit] || '')}
                                        onChange={(e) => {
                                          const newUnits = [...data.outdoor_units_list!];
                                          newUnits[index] = { ...unit, [item.key]: e.target.value };
                                          setData({ ...data, outdoor_units_list: newUnits });
                                        }}
                                        size="small"
                                        placeholder={item.key === "manufacturing_date" ? "YYYY/MM" : ""}
                                      />
                                    )
                                  ) : (
                                    <Typography>{String(unit[item.key as keyof typeof unit] || '')}</Typography>
                                  )
                                )}
                              </TableCell>
                            </TableRow>
                          ))}

                          {/* 作業前チェック */}
                          <TableRow>
                            <StyledTableCell colSpan={2} className="section-header">
                              作業前チェック
                            </StyledTableCell>
                          </TableRow>
                          {[
                            { label: "異音振動", key: "pre_noise_vibration" },
                            { label: "破損・凹み", key: "pre_damage_dent" },
                            { label: "熱交換器", key: "pre_heat_exchanger" },
                            { label: "外装清掃", key: "pre_external_cleaning" },
                          ].map((item) => (
                            <TableRow key={item.key}>
                              <StyledTableCell component="th" scope="row" className="item-label">
                                {item.label}
                              </StyledTableCell>
                              <TableCell>
                                <Box display="flex" gap={2}>
                                  {isEditing ? (
                                    <Box display="flex" gap={2}>
                                      <WorkCheckButton
                                        value="◯"
                                        isActive={unit[item.key as keyof typeof unit] === WORK_CHECK_OPTIONS.GOOD}
                                        onClick={() => {
                                          const newUnits = [...data.outdoor_units_list!];
                                          newUnits[index] = { ...unit, [item.key]: WORK_CHECK_OPTIONS.GOOD };
                                          setData({ ...data, outdoor_units_list: newUnits });
                                        }}
                                      />
                                      <WorkCheckButton
                                        value="△"
                                        isActive={unit[item.key as keyof typeof unit] === WORK_CHECK_OPTIONS.FAIR}
                                        onClick={() => {
                                          const newUnits = [...data.outdoor_units_list!];
                                          newUnits[index] = { ...unit, [item.key]: WORK_CHECK_OPTIONS.FAIR };
                                          setData({ ...data, outdoor_units_list: newUnits });
                                        }}
                                      />
                                      <WorkCheckButton
                                        value="×"
                                        isActive={unit[item.key as keyof typeof unit] === WORK_CHECK_OPTIONS.BAD}
                                        onClick={() => {
                                          const newUnits = [...data.outdoor_units_list!];
                                          newUnits[index] = { ...unit, [item.key]: WORK_CHECK_OPTIONS.BAD };
                                          setData({ ...data, outdoor_units_list: newUnits });
                                        }}
                                      />
                                      <WorkCheckButton
                                        value="ー"
                                        isActive={unit[item.key as keyof typeof unit] === WORK_CHECK_OPTIONS.NONE}
                                        onClick={() => {
                                          const newUnits = [...data.outdoor_units_list!];
                                          newUnits[index] = { ...unit, [item.key]: WORK_CHECK_OPTIONS.NONE };
                                          setData({ ...data, outdoor_units_list: newUnits });
                                        }}
                                      />
                                    </Box>
                                  ) : (
                                    <Typography>
                                      {formatValue(unit[item.key as keyof typeof unit]) || WORK_CHECK_OPTIONS.NONE}
                                    </Typography>
                                  )}
                                </Box>
                              </TableCell>
                            </TableRow>
                          ))}

                          {/* 作業後チェック */}
                          <TableRow>
                            <StyledTableCell colSpan={2} className="section-header">
                              作業後チェック
                            </StyledTableCell>
                          </TableRow>
                          <TableRow>
                            <StyledTableCell component="th" scope="row" className="item-label">
                              異音・振動
                            </StyledTableCell>
                            <TableCell>
                              <Box display="flex" gap={2}>
                                {isEditing ? (
                                  <Box display="flex" gap={2}>
                                    <WorkCheckButton
                                      value="◯"
                                      isActive={unit.post_noise_vibration === WORK_CHECK_OPTIONS.GOOD}
                                      onClick={() => {
                                        const newUnits = [...data.outdoor_units_list!];
                                        newUnits[index] = { ...unit, post_noise_vibration: WORK_CHECK_OPTIONS.GOOD };
                                        setData({ ...data, outdoor_units_list: newUnits });
                                      }}
                                    />
                                    <WorkCheckButton
                                      value="△"
                                      isActive={unit.post_noise_vibration === WORK_CHECK_OPTIONS.FAIR}
                                      onClick={() => {
                                        const newUnits = [...data.outdoor_units_list!];
                                        newUnits[index] = { ...unit, post_noise_vibration: WORK_CHECK_OPTIONS.FAIR };
                                        setData({ ...data, outdoor_units_list: newUnits });
                                      }}
                                    />
                                    <WorkCheckButton
                                      value="×"
                                      isActive={unit.post_noise_vibration === WORK_CHECK_OPTIONS.BAD}
                                      onClick={() => {
                                        const newUnits = [...data.outdoor_units_list!];
                                        newUnits[index] = { ...unit, post_noise_vibration: WORK_CHECK_OPTIONS.BAD };
                                        setData({ ...data, outdoor_units_list: newUnits });
                                      }}
                                    />
                                    <WorkCheckButton
                                      value="ー"
                                      isActive={unit.post_noise_vibration === WORK_CHECK_OPTIONS.NONE}
                                      onClick={() => {
                                        const newUnits = [...data.outdoor_units_list!];
                                        newUnits[index] = { ...unit, post_noise_vibration: WORK_CHECK_OPTIONS.NONE };
                                        setData({ ...data, outdoor_units_list: newUnits });
                                      }}
                                    />
                                  </Box>
                                ) : (
                                  <Typography>
                                    {unit.post_noise_vibration || WORK_CHECK_OPTIONS.NONE}
                                  </Typography>
                                )}
                              </Box>
                            </TableCell>
                          </TableRow>

                          {/* 写真セクション - 基本情報の後に移動 */}
                          <TableRow>
                            <StyledTableCell colSpan={2} className="section-header">
                              写真
                            </StyledTableCell>
                          </TableRow>
                          <TableRow>
                            <TableCell colSpan={2}>
                              {unit.photo_sets?.map((photoSet, photoIndex) => (
                                <Box key={photoSet.id} mb={photoIndex < (unit.photo_sets?.length || 0) - 1 ? 4 : 0}>
                                  <Table>
                                    <TableBody>
                                      {/* 写真セットのヘッダー行に削除ボタンを追加 */}
                                      <TableRow>
                                        <StyledTableCell colSpan={2} className="section-header">
                                          <Box
                                            display="flex"
                                            justifyContent="space-between"
                                            alignItems="center"
                                            width="100%"
                                          >
                                            <Typography variant="subtitle1">
                                              写真セット {photoIndex + 1}
                                            </Typography>
                                            {isEditing && (
                                              <Button
                                                startIcon={<Delete />}
                                                color="error"
                                                size="small"
                                                onClick={(e) => {
                                                  e.stopPropagation();
                                                  handleDeleteClick('outdoor_photo_set', photoIndex, index);
                                                }}
                                                sx={{ ml: 2 }}
                                              >
                                                削除
                                              </Button>
                                            )}
                                          </Box>
                                        </StyledTableCell>
                                      </TableRow>
                                      <TableRow>
                                        <StyledTableCell component="th" scope="row" className="item-label">
                                          タイトル
                                        </StyledTableCell>
                                        <TableCell>
                                          {isEditing ? (
                                            <TextField
                                              fullWidth
                                              value={photoSet.title || ''}
                                              onChange={(e) => {
                                                const newPhotoSets = [...(unit.photo_sets || [])];
                                                newPhotoSets[photoIndex] = {
                                                  ...photoSet,
                                                  title: e.target.value,
                                                };
                                                const newUnits = [...data.outdoor_units_list!];
                                                newUnits[index] = { ...unit, photo_sets: newPhotoSets };
                                                setData({ ...data, outdoor_units_list: newUnits });
                                              }}
                                              size="small"
                                            />
                                          ) : (
                                            <Typography>{photoSet.title || ''}</Typography>
                                          )}
                                        </TableCell>
                                      </TableRow>
                                      <TableRow>
                                        <TableCell colSpan={2}>
                                          {renderPhotoSetContent(photoSet, (updatedPhotoSet) => {
                                            const newPhotoSets = [...(unit.photo_sets || [])];
                                            newPhotoSets[photoIndex] = updatedPhotoSet;
                                            const newUnits = [...data.outdoor_units_list!];
                                            newUnits[index] = { ...unit, photo_sets: newPhotoSets };
                                            setData({ ...data, outdoor_units_list: newUnits });
                                          })}
                                        </TableCell>
                                      </TableRow>
                                    </TableBody>
                                  </Table>
                                </Box>
                              ))}
                              {isEditing && (
                                <Box display="flex" justifyContent="flex-end" mt={2}>
                                  <Button
                                    variant="contained"
                                    color="primary"
                                    onClick={() => {
                                      const newPhotoSet: OutdoorUnitPhotoSetResponse = {
                                        id: Date.now(),
                                        title: "",
                                        before_image_path: null,
                                        before_comment: "",
                                        after_image_path: null,
                                        after_comment: "",
                                      };
                                      const newPhotoSets = [...(unit.photo_sets || []), newPhotoSet];
                                      const newUnits = [...data.outdoor_units_list!];
                                      newUnits[index] = { ...unit, photo_sets: newPhotoSets };
                                      setData({ ...data, outdoor_units_list: newUnits });
                                    }}
                                    startIcon={<Add />}
                                    size="small"
                                  >
                                    写真を追加
                                  </Button>
                                </Box>
                              )}
                            </TableCell>
                          </TableRow>

                        </TableBody>
                      </Table>
                    </AccordionDetails>
                  </StyledAccordion>
                ))}
                {renderSectionFooter(addOutdoorUnit, "外機を追加")}
              </AccordionDetails>
            </StyledAccordion>
          )}

          {/* その他作業情報 */}
          {/* その他作業情報 */}
          <StyledAccordion defaultExpanded>
            <StyledAccordionSummary expandIcon={<ExpandMore />}>
              <Typography variant="h6">その他作業情報</Typography>
            </StyledAccordionSummary>
            <AccordionDetails>
              {data.other_works?.map((work, index) => (
                <StyledAccordion
                  key={work.id}
                  expanded={expandedUnits[`other-${work.id}`] ?? false}
                  onChange={handleUnitAccordionChange('other', work.id)}
                  sx={{ mb: index < data.other_works!.length - 1 ? 2 : 0 }}
                >
                  <StyledAccordionSummary expandIcon={<ExpandMore />}>
                    <SectionHeader index={index} type="other" />
                  </StyledAccordionSummary>
                  <AccordionDetails>
                    <Table>
                      <TableBody>
                        {/* 基本情報 */}
                        <TableRow>
                          <StyledTableCell colSpan={2} className="section-header">
                            基本情報
                          </StyledTableCell>
                        </TableRow>
                        <TableRow>
                          <StyledTableCell component="th" scope="row" className="item-label">
                            作業名称
                          </StyledTableCell>
                          <TableCell>
                            {isEditing ? (
                              <Autocomplete<string, false, false, true>
                                freeSolo
                                loading={workTypeLoading}
                                options={workTypeOptions}
                                value={String(work.work_type || '')}
                                inputValue={String(work.work_type || '')}
                                onChange={(_, newValue) => {
                                  const newWorks = [...data.other_works!];
                                  // 作業名称マスタから選択された場合はIDも設定
                                  const selectedMaster = workTypeOptionsWithId.find(option => option.name === newValue);
                                  newWorks[index] = { 
                                    ...work, 
                                    work_type: newValue || '',
                                    work_type_id: selectedMaster ? selectedMaster.id : null
                                  };
                                  setData({ ...data, other_works: newWorks });
                                }}
                                onInputChange={(_, newInputValue) => {
                                  const newWorks = [...data.other_works!];
                                  // 自由入力の場合はIDをnullに設定
                                  const selectedMaster = workTypeOptionsWithId.find(option => option.name === newInputValue);
                                  newWorks[index] = { 
                                    ...work, 
                                    work_type: newInputValue || '',
                                    work_type_id: selectedMaster ? selectedMaster.id : null
                                  };
                                  setData({ ...data, other_works: newWorks });
                                }}
                                renderInput={(params) => (
                                  <TextField
                                    {...params}
                                    fullWidth
                                    size="small"
                                  />
                                )}
                              />
                            ) : (
                              <Typography>{String(work.work_type || '')}</Typography>
                            )}
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <StyledTableCell component="th" scope="row" className="item-label">
                            作業内容
                          </StyledTableCell>
                          <TableCell>
                            {isEditing ? (
                              <TextField
                                fullWidth
                                multiline
                                rows={3}
                                value={work.details || ''}
                                onChange={(e) => {
                                  const newWorks = [...data.other_works!];
                                  newWorks[index] = { ...work, details: e.target.value };
                                  setData({ ...data, other_works: newWorks });
                                }}
                                size="small"
                              />
                            ) : (
                              work.details || ''
                            )}
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <StyledTableCell component="th" scope="row" className="item-label">
                            数量
                          </StyledTableCell>
                          <TableCell>
                            {renderEditableField(
                              work.quantity,
                              (value) => {
                                if (data.other_works) {
                                  const newItems = [...data.other_works];
                                  newItems[index] = { ...work, quantity: Number(value) };
                                  setData({ ...data, other_works: newItems });
                                }
                              },
                              'number'
                            )}
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <StyledTableCell component="th" scope="row" className="item-label">
                            単位
                          </StyledTableCell>
                          <TableCell>
                            {renderEditableField(
                              work.unit,
                              (value) => {
                                if (data.other_works) {
                                  const newItems = [...data.other_works];
                                  newItems[index] = { ...work, unit: value };
                                  setData({ ...data, other_works: newItems });
                                }
                              }
                            )}
                          </TableCell>
                        </TableRow>

                        {/* 写真セクション */}
                        <TableRow>
                          <StyledTableCell colSpan={2} className="section-header">
                            写真
                          </StyledTableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell colSpan={2}>
                            {work.photo_sets?.map((photoSet, photoIndex) => (
                              <Box key={photoSet.id} mb={photoIndex < (work.photo_sets?.length || 0) - 1 ? 4 : 0}>
                                <Table>
                                  <TableBody>
                                    {/* 写真セットのヘッダー行に削除ボタンを追加 */}
                                    <TableRow>
                                      <StyledTableCell colSpan={2} className="section-header">
                                        <Box
                                          display="flex"
                                          justifyContent="space-between"
                                          alignItems="center"
                                          width="100%"
                                        >
                                          <Typography variant="subtitle1">
                                            写真セット {photoIndex + 1}
                                          </Typography>
                                          {isEditing && (
                                            <Button
                                              startIcon={<Delete />}
                                              color="error"
                                              size="small"
                                              onClick={(e) => {
                                                e.stopPropagation();
                                                handleDeleteClick('other_photo_set', photoIndex, index);
                                              }}
                                              sx={{ ml: 2 }}
                                            >
                                              削除
                                            </Button>
                                          )}
                                        </Box>
                                      </StyledTableCell>
                                    </TableRow>
                                    <TableRow>
                                      <StyledTableCell component="th" scope="row" className="item-label">
                                        タイトル
                                      </StyledTableCell>
                                      <TableCell>
                                        {isEditing ? (
                                          <TextField
                                            fullWidth
                                            value={photoSet.title || ''}
                                            onChange={(e) => {
                                              const newPhotoSets = [...(work.photo_sets || [])];
                                              newPhotoSets[photoIndex] = {
                                                ...photoSet,
                                                title: e.target.value,
                                              };
                                              const newWorks = [...data.other_works!];
                                              newWorks[index] = { ...work, photo_sets: newPhotoSets };
                                              setData({ ...data, other_works: newWorks });
                                            }}
                                            size="small"
                                          />
                                        ) : (
                                          <Typography>{photoSet.title || ''}</Typography>
                                        )}
                                      </TableCell>
                                    </TableRow>
                                    <TableRow>
                                      <TableCell colSpan={2}>
                                        {renderPhotoSetAnoterContent(photoSet, (updatedPhotoSet) => {
                                          const newPhotoSets = [...(work.photo_sets || [])];
                                          newPhotoSets[photoIndex] = updatedPhotoSet;
                                          const newWorks = [...data.other_works!];
                                          newWorks[index] = { ...work, photo_sets: newPhotoSets };
                                          setData({ ...data, other_works: newWorks });
                                        })}
                                      </TableCell>
                                    </TableRow>
                                  </TableBody>
                                </Table>
                              </Box>
                            ))}
                            {isEditing && (
                              <Box display="flex" justifyContent="flex-end" mt={2}>
                                <Button
                                  variant="contained"
                                  color="primary"
                                  onClick={() => {
                                    const newPhotoSet = {
                                      id: Date.now(),
                                      title: "",
                                      before_image_path: null,
                                      before_comment: "",
                                      after_image_path: null,
                                      after_comment: "",
                                    };
                                    const newPhotoSets = [...(work.photo_sets || []), newPhotoSet];
                                    const newWorks = [...data.other_works!];
                                    newWorks[index] = { ...work, photo_sets: newPhotoSets };
                                    setData({ ...data, other_works: newWorks });
                                  }}
                                  startIcon={<Add />}
                                  size="small"
                                >
                                  写真を追加
                                </Button>
                              </Box>
                            )}
                          </TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </AccordionDetails>
                </StyledAccordion>
              ))}
              {renderSectionFooter(addOtherWork, "その他作業を追加")}
            </AccordionDetails>
          </StyledAccordion>

          {/* 報告事項 */}
          <StyledAccordion defaultExpanded>
            <StyledAccordionSummary expandIcon={<ExpandMore />}>
              <Typography variant="h6">報告事項写真</Typography>
            </StyledAccordionSummary>
            <AccordionDetails>
              {data.photo_sets?.map((photo, index) => (
                <Box key={photo.id} mb={index < data.photo_sets!.length - 1 ? 4 : 0}>
                  <Table>
                    <TableBody>
                      <TableRow>
                        <StyledTableCell colSpan={2} className="section-header">
                          <SectionHeader index={index} type="photo" />
                        </StyledTableCell>
                      </TableRow>
                      <TableRow>
                        <StyledTableCell component="th" scope="row" className="item-label">
                          タイトル
                        </StyledTableCell>
                        <TableCell>
                          {isEditing ? (
                            <TextField
                              fullWidth
                              value={photo.title || ''}
                              onChange={(e) => {
                                const newPhotos = [...data.photo_sets!];
                                newPhotos[index] = { ...photo, title: e.target.value };
                                setData({ ...data, photo_sets: newPhotos });
                              }}
                              size="small"
                            />
                          ) : (
                            <Typography>{photo.title || ''}</Typography>
                          )}
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell colSpan={2}>
                          {renderPhotoSetAnoterContent(photo, (updatedPhotoSet) => {
                            const newPhotos = [...data.photo_sets!];
                            newPhotos[index] = updatedPhotoSet;
                            setData({ ...data, photo_sets: newPhotos });
                          })}
                        </TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </Box>
              ))}
              {renderSectionFooter(addPhotoSet, "報告事項写真を追加")}
            </AccordionDetails>
          </StyledAccordion>


          {/* 見取り図情報 */}
          <StyledAccordion defaultExpanded>
            <StyledAccordionSummary expandIcon={<ExpandMore />}>
              <Typography variant="h6">見取り図情報</Typography>
            </StyledAccordionSummary>
            <AccordionDetails>
              {data.floor_plans?.map((plan, index) => (
                <Box key={plan.id} mb={index < data.floor_plans!.length - 1 ? 4 : 0}>
                  <Table>
                    <TableBody>
                      <TableRow>
                        <StyledTableCell colSpan={2} className="section-header">
                          <SectionHeader index={index} type="floor_plan" />
                        </StyledTableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                  <Table>
                    <TableBody>
                      <TableRow>
                        <StyledTableCell component="th" scope="row" className="item-label">
                          見取り図データ
                        </StyledTableCell>
                        <TableCell>
                          {isEditing ? (
                            <FloorPlanEditorWrapper
                              key={`floor-plan-${plan.id}`}
                              initialData={{
                                id: plan.id, // 明示的にidを渡す
                                title: plan.title || '',
                                json_data: plan.json_data || null,
                                image_data: plan.image_data,
                                background_image_url: plan.background_image_url,
                                floor_plan_image_url: plan.floor_plan_image_url
                              }}
                              editorRef={(ref) => {
                                if (plan.id && ref) {
                                  // 参照が変更された場合のみ更新
                                  if (floorPlanEditorRefs.current[plan.id] !== ref) {
                                    floorPlanEditorRefs.current[plan.id] = ref;
                                  }
                                }
                              }}
                              onChange={(updatedPlan) => {
                                // まず最新の状態を取得するために関数形式でsetDataを使用
                                setData(prevData => {
                                  if (!prevData || !prevData.floor_plans) return prevData;

                                  // floor_plansのディープコピーを作成（重要）
                                  const newFloorPlans = JSON.parse(JSON.stringify(prevData.floor_plans));

                                  // 更新対象のプランを特定
                                  const planIndex = newFloorPlans.findIndex((p: { id: number; }) => p.id === plan.id);
                                  if (planIndex === -1) return prevData;

                                  // 対象プランの更新（新しいコピーを作成）
                                  newFloorPlans[planIndex] = {
                                    ...newFloorPlans[planIndex], // 既存のプロパティを維持
                                    title: updatedPlan.title,
                                    json_data: updatedPlan.json_data, // 更新されたJSONデータ
                                    background_image_url: updatedPlan.background_image_url,
                                    floor_plan_image_url: updatedPlan.floor_plan_image_url
                                  };

                                  // 新しい状態を返す（immutableに保つ）
                                  return {
                                    ...prevData,
                                    floor_plans: newFloorPlans
                                  };
                                });
                              }}
                            />
                          ) : (
                            // floor_plan_image_urlを優先して表示し、なければimage_dataを表示
                            plan.floor_plan_image_url ? (
                              <img
                                src={plan.floor_plan_image_url}
                                alt={plan.title ?? ''}
                                style={{ maxWidth: '600px', maxHeight: '250px' }}
                              />
                            ) : plan.image_data && (
                              <img
                                src={`data:image/jpeg;base64,${plan.image_data}`}
                                alt={plan.title ?? ''}
                                style={{ maxWidth: '600px', maxHeight: '250px' }}
                              />
                            )
                          )}
                        </TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </Box>
              ))}
              {/* 見取り図を追加するボタン */}
              {isEditing && (
                <Box display="flex" justifyContent="flex-end" mt={2}>
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={() => {
                      const newPlan = {
                        id: Date.now(),
                        title: "見取り図 " + (data.floor_plans?.length ? data.floor_plans.length + 1 : 1),
                        json_data: JSON.stringify([]),
                        image_data: null
                      };
                      setData({ ...data, floor_plans: [...(data.floor_plans || []), newPlan] });
                    }}
                    startIcon={<Add />}
                    size="small"
                  >
                    見取り図を追加
                  </Button>
                </Box>
              )}
            </AccordionDetails>
          </StyledAccordion>

          <StyledAccordion defaultExpanded>
            <StyledAccordionSummary expandIcon={<ExpandMore />}>
              <Typography variant="h6">報告事項</Typography>
            </StyledAccordionSummary>
            <AccordionDetails>
              <Table>
                <TableBody>
                  <TableRow>
                    <StyledTableCell component="th" scope="row" className="item-label">
                      報告事項
                    </StyledTableCell>
                    <TableCell>
                      {isEditing ? (
                        <TextField
                          fullWidth
                          multiline
                          rows={3}
                          value={data.points_to_note || ''}
                          onChange={(e) => setData({ ...data, points_to_note: e.target.value })}
                          size="small"
                        />
                      ) : (
                        data.points_to_note || ''
                      )}
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </AccordionDetails>
          </StyledAccordion>

          {/* 作業者メモ欄 */}
          <StyledAccordion defaultExpanded>
            <StyledAccordionSummary expandIcon={<ExpandMore />}>
              <Typography variant="h6">作業者メモ</Typography>
            </StyledAccordionSummary>
            <AccordionDetails>
              <Table>
                <TableBody>
                  <TableRow>
                    <StyledTableCell component="th" scope="row" className="item-label">
                      作業者メモ
                    </StyledTableCell>
                    <TableCell>
                      {isEditing ? (
                        <TextField
                          fullWidth
                          multiline
                          rows={4}
                          value={data.worker_memo || ''}
                          onChange={(e) => setData({ ...data, worker_memo: e.target.value })}
                          size="small"
                        />
                      ) : (
                        <Typography
                          component="div"
                          sx={{ whiteSpace: 'pre-wrap' }}
                        >
                          {data.worker_memo || ''}
                        </Typography>
                      )}
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
              <Box display="flex" justifyContent="flex-end" mt={1}>
                <Typography
                  variant="caption"
                  color="textSecondary"
                >
                  ※報告書には印字されません
                </Typography>
              </Box>
            </AccordionDetails>
          </StyledAccordion>

          {/* サイン画像 */}
          <StyledAccordion defaultExpanded>
            <StyledAccordionSummary expandIcon={<ExpandMore />}>
              <Typography variant="h6">サイン画像</Typography>
            </StyledAccordionSummary>
            <AccordionDetails>
              <Table>
                <TableBody>
                  <TableRow>
                    <StyledTableCell component="th" scope="row" className="item-label">
                      サイン画像
                    </StyledTableCell>
                    <TableCell>
                      {data.signature && (
                        <img
                          src={`data:image/jpeg;base64,${data.signature}`}
                          alt="サイン"
                          style={{ maxWidth: '100%', maxHeight: '200px' }}
                        />
                      )}
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </AccordionDetails>
          </StyledAccordion>

          {/* メモ欄 */}
          <StyledAccordion defaultExpanded>
            <StyledAccordionSummary expandIcon={<ExpandMore />}>
              <Typography variant="h6">管理者メモ</Typography>
            </StyledAccordionSummary>
            <AccordionDetails>
              <Table>
                <TableBody>
                  <TableRow>
                    <StyledTableCell component="th" scope="row" className="item-label">
                      管理者メモ
                    </StyledTableCell>
                    <TableCell>
                      {isEditing ? (
                        <TextField
                          fullWidth
                          multiline
                          rows={4}
                          value={data.memo || ''}
                          onChange={(e) => setData({ ...data, memo: e.target.value })}
                          size="small"
                        />
                      ) : (
                        <Typography
                          component="div"
                          sx={{ whiteSpace: 'pre-wrap' }}
                        >
                          {data.memo || ''}
                        </Typography>
                      )}
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
              <Box display="flex" justifyContent="flex-end" mt={1}>
                <Typography
                  variant="caption"
                  color="textSecondary"
                >
                  ※報告書には印字されません
                </Typography>
              </Box>
            </AccordionDetails>
          </StyledAccordion>
        </>
      )}
    </PageContainer>
  );
};

export default ReportAirconDetail;